'use client';

import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import dynamic from 'next/dynamic';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import Image from 'next/image';
import { useTranslations, useFormatter, useLocale } from 'next-intl';
import {
  Eye,
  MapPin,
  Calendar,
  Users,
  Shield,
  Check,
  CheckCircle2,
  Heart,
  CreditCard,
  Package,
  Plus,
  Truck,
  AlertCircle,
  FileText,
  List,
  ExternalLink,
  Loader2,
  Bike,
  Lock,
} from 'lucide-react';
import { CompletionScreen } from './components/CompletionScreen';
import { StepIndicator } from './components/StepIndicator';
import { NavigationBar } from './components/NavigationBar';
import type { CheckoutCatalogItem, CheckoutSessionResult } from '@impact/types';
import { SETTLED_DONATION_STATUSES } from '@impact/types/enums';
import type { PaymentCompletedData, PaymentFailedData } from '@adyen/adyen-web';
import { useRegional } from '@/components/providers/regional-provider';
import { trpc, trpcVanilla } from '@/lib/trpc';
import { AuthStep } from '@/tenants/_shared/components/registration';
import { useRegistrationStore } from '@/tenants/_shared/stores/registration-store';
import { useSessionReplay } from '@/hooks/use-session-replay';
import {
  trackRego,
  describeSaveError,
  type RegoContext,
} from '@/tenants/_shared/lib/registration-analytics';
import { trackDonation } from '@/tenants/_shared/lib/donation-analytics';
import { useTenantStore } from '@/stores/tenant-store';
import {
  useActiveCampaignClosed,
  useCountries,
  useStatesWithRides,
  useRidesByLocation,
} from '@/tenants/_shared/hooks/use-public-lookups';
import { CURRENCY_OPTIONS } from '@/tenants/_shared/lib/currencies';
import { usePaymentConfig } from '@/tenants/_shared/hooks/usePaymentConfig';
import { useSessionRecovery } from '@/tenants/_shared/hooks/useSessionRecovery';
import { useRegistrationAuth } from '@/tenants/_shared/hooks/useRegistrationAuth';
import { useRegistrationInit } from './hooks/useRegistrationInit';
import {
  clearAdyenRedirectSession,
  loadAdyenRedirectSession,
  saveAdyenRedirectSession,
  saveInFlightPayment,
  clearInFlightPayment,
} from '@/tenants/_shared/lib/adyenRedirectSession';
import { toIsoCountryCode } from '@/tenants/_shared/lib/normalizeCountryCode';
import {
  buildCheckoutItemsInput,
  getCheckoutItemsSubtotal,
  getSelectedCheckoutItems,
  selectedCheckoutItemsRequireShipping,
} from '@/tenants/_shared/lib/itemCheckout';
import { ADDRESS_REQUIRED_THRESHOLDS } from '@/tenants/_shared/hooks/useItemizedCheckoutController';
import {
  getRegistrationDonationFloor,
  getRegistrationDonationPresets,
  getRegistrationOrderShortfall,
  isRegistrationMinimumMet,
} from '@/tenants/_shared/lib/registrationCheckout';
import {
  buildRegistrationCompletionState,
  resolveRegistrationRedirectFlow,
  type RegistrationCompletionState,
} from '@/tenants/_shared/lib/registrationCheckoutFlow';
import {
  getMissingRegistrationShippingFields,
  type RegistrationShippingField,
} from '@/tenants/_shared/lib/registration-shipping-validation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input/input';
import { Label } from '@/components/ui/label/label';
import { Checkbox } from '@/components/ui/checkbox/checkbox';
import { Switch } from '@/components/ui/switch/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog/dialog';
import { Badge } from '@/components/ui/badge/badge';
import { CountrySelect } from '@/components/ui/country-select/country-select';
import { Combobox } from '@/components/ui/combobox/combobox';
import {
  DonorAddressFields,
  type Address as DonorAddress,
} from '@/tenants/_shared/components/DonorAddressFields';
import { useDeviceCapabilities } from '@/hooks/use-device-capabilities';
import { useScrollToFocusedInput } from '@/hooks/use-scroll-to-focused-input';
import { useScrollIntoViewOnReveal } from '@/hooks/use-scroll-into-view-on-reveal';

const AdyenCheckout = dynamic(
  () =>
    import('@/tenants/_shared/components/AdyenCheckout').then((module) => ({
      default: module.AdyenCheckout,
    })),
  { ssr: false },
);

const PayPalCheckout = dynamic(
  () =>
    import('@/tenants/_shared/components/PayPalCheckout').then((m) => ({
      default: m.PayPalCheckout,
    })),
  { ssr: false },
);

// Eagerly prefetch Adyen chunk + preconnect to Adyen hosts so the Drop-in
// renders instantly when "Continue to Payment" is clicked.
if (typeof window !== 'undefined') {
  void import('@/tenants/_shared/components/AdyenCheckout');

  const adyenEnv = process.env.NEXT_PUBLIC_ADYEN_ENVIRONMENT === 'live' ? 'live' : 'test';
  for (const host of [
    `https://checkoutshopper-${adyenEnv}.adyen.com`,
    `https://checkoutshopper-${adyenEnv}.cdn.adyen.com`,
  ]) {
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = host;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  }
}

// Prefetched data from RSC for faster initial render
interface PrefetchedData {
  countries: Array<{ id: string; code: string; name: string }>;
  states: Array<{ id: string; code: string; name: string }>;
  detectedCountry: string | null;
}

interface RegisterPageProps {
  prefetchedData?: PrefetchedData;
}

const currencies = CURRENCY_OPTIONS;

// Map country slugs to country codes for URL parameter handling
// Keep this for URL param parsing (e.g., /register?country=australia)
const countrySlugToCode: Record<string, string> = {
  australia: 'AU',
  canada: 'CA',
  germany: 'DE',
  spain: 'ES',
  france: 'FR',
  england: 'XE',
  scotland: 'XS',
  wales: 'XW',
  'northern-ireland': 'XI',
  'united-kingdom': 'GB',
  uk: 'GB',
  italy: 'IT',
  japan: 'JP',
  netherlands: 'NL',
  'new-zealand': 'NZ',
  'united-states': 'US',
  usa: 'US',
};

// Location data types returned from API
interface CountryData {
  id: string;
  code: string;
  name: string;
}

interface StateData {
  id: string;
  code: string;
  name: string;
}

interface RideData {
  id: string;
  name: string;
  slug: string;
  capacity: string | null;
  minimumDonation: string | null;
  currentRegistrations: number;
  isPostponed?: boolean;
  isCancelled?: boolean;
  postponedDate?: string | null;
}

type RegistrationCheckoutStep =
  | 'editing'
  | 'creating-session'
  | 'payment'
  | 'finalizing'
  | 'awaiting-settlement'
  | 'voucher-pending'
  | 'failed';

// Merchandise items
// Suggested donation amounts
const REGISTRATION_DONATION_PRESETS_USD = [25, 50, 100, 250];
const REGISTRATION_FINALIZE_TIMEOUT_MS = 30_000;
const SETTLEMENT_POLL_INTERVAL_MS = 3_000;
const SETTLEMENT_POLL_TIMEOUT_MS = 60_000;

// Helper function to get initial country from URL params
// Note: State/ride validation now happens dynamically via API
function getInitialCountryFromParams(searchParams: {
  get: (key: string) => string | null;
}): string {
  const countryParam = searchParams.get('country');
  if (!countryParam) return '';
  // Convert slug to code if needed
  return countrySlugToCode[countryParam.toLowerCase()] || countryParam.toUpperCase();
}

function parseRideMinimumDonation(minimumDonation: string | null | undefined): number {
  if (!minimumDonation) return 0;
  const parsed = Number(minimumDonation);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

function getCapacityBadgeValue(capacity: string | null | undefined): string | null {
  if (!capacity) return null;
  const trimmed = capacity.trim();
  if (!trimmed || trimmed === '0') return null;
  return trimmed;
}

function parseRideCapacity(capacity: string | null | undefined): number | null {
  if (!capacity) return null;
  const parsed = Number(capacity);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export function RegisterPage({ prefetchedData }: RegisterPageProps) {
  const locale = useLocale();
  const t = useTranslations('register');
  const tDonate = useTranslations('donate');
  const tForm = useTranslations('donate.form');
  const tWaiver = useTranslations('waiver');
  const tCommon = useTranslations('common');
  const format = useFormatter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const formPanelRef = useRef<HTMLDivElement>(null);
  const step1StateRef = useRef<HTMLDivElement>(null);
  const step1RideRef = useRef<HTMLDivElement>(null);
  const step1WaitlistRef = useRef<HTMLDivElement>(null);
  const step1WaiverRef = useRef<HTMLDivElement>(null);
  const step4ShippingRef = useRef<HTMLDivElement>(null);
  const step4PaymentRef = useRef<HTMLDivElement>(null);
  const { isMobile, isKeyboardOpen } = useDeviceCapabilities();
  useScrollToFocusedInput(formPanelRef, isMobile);
  const urlState = searchParams.get('state') ?? '';
  const urlEventId = searchParams.get('eventId') ?? '';

  // Get initial country from URL params (computed once)
  const initialCountryFromUrl = useMemo(() => {
    return getInitialCountryFromParams(searchParams);
  }, [searchParams]);

  // Currency from regional provider
  const { currency: selectedCurrency } = useRegional();

  // Registration store — checkout form buffer (steps 3-4 only)
  const {
    newsletterOptIn,
    triumphMarketingOptIn,
    movemberMarketingOptIn,
    setNewsletterOptIn,
    setTriumphMarketingOptIn,
    setMovemberMarketingOptIn,
    donationAmount,
    customDonation,
    selectedMerch,
    setDonationAmount,
    setCustomDonation,
    updateMerchQuantity,
    removeMerch,
    shippingAddress,
    shippingAddressLine2,
    shippingCity,
    shippingState,
    shippingPostcode,
    shippingCountry,
    setShipping,
    getActualDonation,
    hydrateFromServer,
    getCheckoutStateSnapshot,
    clearCart,
    reset,
  } = useRegistrationStore();

  // Auth hook — owns signup/login/completeRegistration state + actions
  const registrationAuth = useRegistrationAuth();

  // Registration init hook — replaces mount init + resume/guard useEffects
  const init = useRegistrationInit(initialCountryFromUrl, prefetchedData?.detectedCountry);
  const {
    authUser,
    authenticatedProfile,
    shouldSkipAuthStep,
    paymentReturn: {
      returnDonationId,
      redirectResult,
      sessionId: returnSessionId,
      eventId: returnEventId,
      recoveryToken: returnRecoveryToken,
      isLocalStorageRecovery,
    },
  } = init;

  // Entrant + vehicle from auth hook (populated after signup/login in this session)
  const entrant = registrationAuth.entrant;
  const vehicle = registrationAuth.vehicle;

  // ── Step 1: Ride selection — URL params + component state ──
  const [rideCountryCode, setRideCountryCode] = useState(
    initialCountryFromUrl || prefetchedData?.detectedCountry || '',
  );
  const [rideStateCode, setRideStateCode] = useState(urlState);
  const [rideId, setRideId] = useState('');
  const [rideName, setRideName] = useState('');
  const [waiverAccepted, setWaiverAccepted] = useState(false);
  const [joinWaitlist, setJoinWaitlist] = useState(false);

  // ── Navigation — component state ──
  const [currentStep, setCurrentStep] = useState(init.initialStep);
  const termsAcceptedForRegistration = waiverAccepted || currentStep > 1;

  // Sync ride selection to URL for shareability
  const updateRideUrl = useCallback(
    (country: string, state: string, eventSlug: string) => {
      const params = new URLSearchParams(searchParams.toString());
      if (country) params.set('country', country);
      else params.delete('country');
      if (state) params.set('state', state);
      else params.delete('state');
      if (eventSlug) params.set('eventId', eventSlug);
      else params.delete('eventId');
      params.delete('intent');

      const query = params.toString();
      router.replace(query ? `${pathname}?${query}` : pathname, { scroll: false });
    },
    [pathname, router, searchParams],
  );

  // Cascading setters that mirror the old store behavior
  const setRideCountry = useCallback(
    (code: string) => {
      setRideCountryCode(code);
      setRideStateCode('');
      setRideId('');
      setRideName('');
      setJoinWaitlist(false);
      updateRideUrl(code, '', '');
    },
    [updateRideUrl],
  );

  const setRideState = useCallback(
    (code: string) => {
      setRideStateCode(code);
      setRideId('');
      setRideName('');
      setJoinWaitlist(false);
      updateRideUrl(rideCountryCode, code, '');
    },
    [rideCountryCode, updateRideUrl],
  );

  const setRide = useCallback(
    (id: string, name: string, eventSlug = '') => {
      setRideId(id);
      setRideName(name);
      setJoinWaitlist(false);
      updateRideUrl(rideCountryCode, rideStateCode, eventSlug);
    },
    [rideCountryCode, rideStateCode, updateRideUrl],
  );

  // Apply init state when auth + guard resolve. Step navigation re-applies when
  // init.initialStep changes, but checkout state is only hydrated ONCE to avoid
  // a late router.refresh() overwriting user edits with stale server state.
  const lastAppliedStep = useRef<number | null>(null);
  const hasHydratedCheckout = useRef(false);
  useEffect(() => {
    if (init.isLoading) return;
    if (lastAppliedStep.current === init.initialStep) return;
    lastAppliedStep.current = init.initialStep;

    setCurrentStep(init.initialStep);

    if (init.pendingRegistration) {
      const intent = searchParams.get('intent');
      setRideCountryCode(init.pendingRegistration.countryCode);
      setRideStateCode(init.pendingRegistration.stateCode ?? '');

      if (intent === 'changeRide') {
        setRideId('');
        setRideName('');
        setJoinWaitlist(false);
        updateRideUrl(
          init.pendingRegistration.countryCode,
          init.pendingRegistration.stateCode ?? '',
          '',
        );
      } else {
        setRideId(init.pendingRegistration.eventId);
        setRideName(init.pendingRegistration.eventName ?? '');
        updateRideUrl(
          init.pendingRegistration.countryCode,
          init.pendingRegistration.stateCode ?? '',
          urlEventId,
        );
        if (init.pendingRegistration.checkoutState && !hasHydratedCheckout.current) {
          hasHydratedCheckout.current = true;
          hydrateFromServer(init.pendingRegistration.checkoutState);
        }
      }
    }
  }, [
    hydrateFromServer,
    init.initialStep,
    init.isLoading,
    init.pendingRegistration,
    searchParams,
    updateRideUrl,
    urlEventId,
  ]);

  // Local UI state (dialogs, previews - not persisted)
  const [isSubmittingRegistration, setIsSubmittingRegistration] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [registrationCompleteEmail, setRegistrationCompleteEmail] = useState<string | null>(null);
  const [completedDonationId, setCompletedDonationId] = useState<string | null>(null);

  // IAB session recovery: restore auth session after bank redirect lands in native browser
  const recovery = useSessionRecovery({
    recoveryToken: returnRecoveryToken,
    isRedirectReturn: !!returnDonationId && !!redirectResult,
    isAuthenticated: !!authUser,
  });

  useEffect(() => {
    if (!returnRecoveryToken || !recovery.attempted) return;
    track('rego_session_recovery', {
      outcome: recovery.recovered ? 'succeeded' : 'failed',
      donation_id: returnDonationId ?? undefined,
    });
  }, [returnRecoveryToken, recovery.attempted, recovery.recovered, returnDonationId]);

  const registrationCompletionRef = useRef<string | null>(null);
  const checkoutCompletionRef = useRef<ReturnType<typeof buildRegistrationCompletionState> | null>(
    null,
  );
  const paypalDonationIdRef = useRef<string | null>(null);

  // Tenant slug + receipt polling for success screen
  const tenantSlug = useTenantStore((s) => s.slug);
  const receiptPollStartRef = useRef<number | null>(null);

  if (completedDonationId && !receiptPollStartRef.current) {
    receiptPollStartRef.current = Date.now();
  }

  const RECEIPT_POLL_INTERVAL_MS = 2000;
  const RECEIPT_POLL_MAX_MS = 15_000;

  const { data: receiptStatus } = trpc.publicDonation.getStatus.useQuery(
    { donationId: completedDonationId! },
    {
      enabled: !!completedDonationId,
      refetchInterval: (query) => {
        if (query.state.data?.taxReceiptAvailable) return false;
        const elapsed = Date.now() - (receiptPollStartRef.current ?? Date.now());
        if (elapsed >= RECEIPT_POLL_MAX_MS) return false;
        return RECEIPT_POLL_INTERVAL_MS;
      },
    },
  );

  // Campaign-closed state: rides stay listed (when the tenant enables
  // events.show_closed_campaign_events) but registration must not proceed.
  const { isCampaignClosed, closedMessage: customClosedMessage } = useActiveCampaignClosed();

  // API hooks for cascading lookups
  const {
    data: countriesData = (prefetchedData?.countries ?? []) as CountryData[],
    isLoading: isLoadingCountries,
  } = useCountries();
  const {
    data: statesData = (prefetchedData?.states ?? []) as StateData[],
    isLoading: isLoadingStates,
  } = useStatesWithRides(rideCountryCode);

  // Determine if country has states
  const hasStates = statesData.length > 0;
  const shouldShowStateSelection = Boolean(rideCountryCode) && (isLoadingStates || hasStates);
  const shouldShowRideSelection =
    Boolean(rideCountryCode) &&
    ((hasStates && Boolean(rideStateCode)) || (!hasStates && !isLoadingStates));

  // Fetch rides based on country and optionally state
  const { data: ridesData = [] as RideData[], isLoading: isLoadingRides } = useRidesByLocation(
    rideCountryCode,
    rideStateCode,
    shouldShowStateSelection,
  );

  useEffect(() => {
    if (!urlEventId || rideId || ridesData.length === 0) {
      return;
    }

    const normalizedUrlEventId = urlEventId.toLowerCase();
    const matchedRide = ridesData.find((ride: RideData) => {
      const normalizedName = ride.name.toLowerCase().replace(/\s+/g, '-');
      return (
        ride.slug.toLowerCase() === normalizedUrlEventId ||
        normalizedName === normalizedUrlEventId ||
        ride.id === urlEventId
      );
    });

    if (matchedRide) {
      setRideId(matchedRide.id);
      setRideName(matchedRide.name);
    }
  }, [rideId, ridesData, urlEventId]);

  // Get selected ride data (use store's rideName as fallback for display)
  const selectedRideData = useMemo(
    () =>
      ridesData.find((r: RideData) => r.id === rideId) ??
      (rideId
        ? {
            id: rideId,
            name: rideName,
            slug: '',
            capacity: null,
            minimumDonation: null,
            currentRegistrations: 0,
          }
        : undefined),
    [ridesData, rideId, rideName],
  );

  useEffect(() => {
    if (!rideId || !selectedRideData?.slug || urlEventId === selectedRideData.slug) {
      return;
    }

    updateRideUrl(rideCountryCode, rideStateCode, selectedRideData.slug);
  }, [rideCountryCode, rideId, rideStateCode, selectedRideData?.slug, updateRideUrl, urlEventId]);

  // Local UI state for dialogs (not persisted to store)
  const [waiverDialogOpen, setWaiverDialogOpen] = useState(false);
  const [simplifiedWaiver, setSimplifiedWaiver] = useState(false);

  // Merchandise preview state (local UI state)
  const [merchPreviewOpen, setMerchPreviewOpen] = useState(false);
  const [merchPreviewItem, setMerchPreviewItem] = useState<CheckoutCatalogItem | null>(null);
  const [checkoutSession, setCheckoutSession] = useState<CheckoutSessionResult | null>(null);
  const [redirectSession, setRedirectSession] = useState<{
    donationId: string;
    sessionId: string;
    sessionData: string;
    redirectResult: string;
  } | null>(null);
  const [registrationCheckoutStep, setRegistrationCheckoutStep] =
    useState<RegistrationCheckoutStep>('editing');
  const [showShippingValidation, setShowShippingValidation] = useState(false);
  const [checkoutReady, setCheckoutReady] = useState(false);
  const [finalizingTimedOut, setFinalizingTimedOut] = useState(false);
  const [settlementTimedOut, setSettlementTimedOut] = useState(false);
  const settlementPollingRef = useRef(false);
  const settlementStartRef = useRef<number>(0);
  const settlementPollCountRef = useRef(0);
  const [paymentReceivedButIncomplete, setPaymentReceivedButIncomplete] = useState(false);
  const [includePlatformFee, setIncludePlatformFee] = useState(true);

  const checkoutEventId = returnEventId ?? rideId;
  const registrationRedirectStorage = useMemo(
    () => (returnSessionId ? loadAdyenRedirectSession(returnSessionId) : null),
    [returnSessionId],
  );
  const isRegistrationRedirectReturn =
    (Boolean(returnDonationId && redirectResult) ||
      (isLocalStorageRecovery && !!returnDonationId)) &&
    (!registrationRedirectStorage?.flow || registrationRedirectStorage.flow === 'registration');
  const { data: checkoutCatalog } = trpc.publicPayment.getCheckoutCatalog.useQuery(
    { eventId: checkoutEventId },
    { enabled: !!checkoutEventId },
  );
  const {
    data: paymentConfigData,
    isLoading: isPaymentConfigLoading,
    isFetching: isPaymentConfigFetching,
    isError: isPaymentConfigError,
  } = usePaymentConfig(checkoutEventId || undefined);

  // All active countries for shipping address (not ride-scoped)
  const emptyShippingCountries: Array<{ id: string; code: string; name: string }> = [];
  const allCountriesQuery = trpc.publicLookups.allCountries.useQuery();
  const allShippingCountries = allCountriesQuery.data ?? emptyShippingCountries;
  const googlePlacesApiKey = paymentConfigData?.googlePlacesApiKey;
  const savedProfile = trpc.myProfile.getProfile.useQuery(undefined, {
    enabled: !!authUser,
    retry: false,
    staleTime: Infinity,
  });
  const shippingPrefilledFromProfileRef = useRef(false);

  useEffect(() => {
    if (shippingPrefilledFromProfileRef.current || !savedProfile.data) {
      return;
    }

    if (
      shippingAddress ||
      shippingAddressLine2 ||
      shippingCity ||
      shippingState ||
      shippingPostcode ||
      shippingCountry
    ) {
      shippingPrefilledFromProfileRef.current = true;
      return;
    }

    const profileAddress = {
      address: savedProfile.data.addressLine1 ?? '',
      addressLine2: savedProfile.data.addressLine2 ?? '',
      city: savedProfile.data.city ?? '',
      state: savedProfile.data.stateCode ?? '',
      postcode: savedProfile.data.postalCode ?? '',
      country: savedProfile.data.countryCode ?? '',
    };

    if (!Object.values(profileAddress).some(Boolean)) {
      shippingPrefilledFromProfileRef.current = true;
      return;
    }

    setShipping(profileAddress);
    shippingPrefilledFromProfileRef.current = true;
  }, [
    savedProfile.data,
    setShipping,
    shippingAddress,
    shippingAddressLine2,
    shippingCity,
    shippingCountry,
    shippingPostcode,
    shippingState,
  ]);

  const { data: redirectContext, isError: isRedirectContextError } =
    trpc.publicDonation.getRedirectContext.useQuery(
      { donationId: returnDonationId! },
      {
        enabled: isRegistrationRedirectReturn,
        refetchInterval:
          registrationCheckoutStep === 'awaiting-settlement' ? SETTLEMENT_POLL_INTERVAL_MS : false,
      },
    );
  const createCheckoutSession = trpc.publicDonation.createCheckoutSession.useMutation();
  const createRegistrationSession = trpc.publicDonation.createRegistrationSession.useMutation();
  const capturePayPalOrder = trpc.publicDonation.capturePayPalOrder.useMutation();
  const commitCheckout = trpc.publicDonation.commitCheckout.useMutation();
  const markRedirectFailed = trpc.publicDonation.markRedirectFailed.useMutation();
  const commitCheckoutMutationRef = useRef(commitCheckout.mutateAsync);
  const markRedirectFailedMutationRef = useRef(markRedirectFailed.mutateAsync);

  useEffect(() => {
    commitCheckoutMutationRef.current = commitCheckout.mutateAsync;
  }, [commitCheckout.mutateAsync]);

  useEffect(() => {
    markRedirectFailedMutationRef.current = markRedirectFailed.mutateAsync;
  }, [markRedirectFailed.mutateAsync]);

  // Track step views when currentStep changes
  const stepNames = ['ride_selection', 'auth', 'preferences', 'payment'] as const;
  useEffect(() => {
    track('rego_step_viewed', {
      step: currentStep,
      step_name: stepNames[currentStep - 1],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  // Start session replay once the user passes step 1 (or resumes at step 3+)
  useSessionReplay(currentStep >= 2);

  const selectedRideCapacity = parseRideCapacity(selectedRideData?.capacity);
  const selectedRideCurrentRegistrations = selectedRideData?.currentRegistrations ?? 0;
  const isSelectedCityFull =
    selectedRideCapacity !== null && selectedRideCurrentRegistrations >= selectedRideCapacity;
  const isSelectedRideCancelled = (selectedRideData as RideData | undefined)?.isCancelled ?? false;
  const isSelectedRidePostponed = (selectedRideData as RideData | undefined)?.isPostponed ?? false;
  const selectedRidePostponedDate =
    (selectedRideData as RideData | undefined)?.postponedDate ?? null;
  const selectedRidePostponedDateLabel = (() => {
    if (!selectedRidePostponedDate) return null;
    // Date column comes back as YYYY-MM-DD; build in local time so toLocaleDateString
    // doesn't shift across timezones.
    const m = selectedRidePostponedDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return null;
    const [, y, mo, d] = m.map(Number) as [unknown, number, number, number];
    return new Date(y, mo - 1, d).toLocaleDateString(undefined, {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  })();
  const selectedRideMinimumDonation = parseRideMinimumDonation(selectedRideData?.minimumDonation);
  const mandatoryDonation = selectedRideMinimumDonation;
  const hasMandatoryDonation = mandatoryDonation > 0;

  // Analytics context — passed to all trackRego calls
  const regoCtx: Partial<RegoContext> = useMemo(
    () => ({
      ride_id: rideId || undefined,
      ride_name: rideName || selectedRideData?.name || undefined,
      country_code: rideCountryCode || undefined,
      current_step: currentStep,
      has_mandatory_donation: hasMandatoryDonation,
    }),
    [rideId, rideName, selectedRideData?.name, rideCountryCode, currentStep, hasMandatoryDonation],
  );

  // Local wrapper that auto-includes rego context on every event
  const track: typeof trackRego = useCallback(
    (event, properties, extraContext) => {
      trackRego(event, properties, { ...regoCtx, ...extraContext });
    },
    [regoCtx],
  );

  const shouldSkipPaymentStep = isSelectedCityFull && joinWaitlist;
  const shouldShowWaitlistToggle =
    Boolean(rideId) && isSelectedCityFull && !isSelectedRideCancelled;
  const shouldShowWaiverCard = Boolean(rideId);

  useScrollIntoViewOnReveal(step1StateRef, currentStep === 1 && shouldShowStateSelection, {
    enabled: isMobile,
  });
  useScrollIntoViewOnReveal(step1RideRef, currentStep === 1 && shouldShowRideSelection, {
    enabled: isMobile,
  });
  useScrollIntoViewOnReveal(step1WaitlistRef, currentStep === 1 && shouldShowWaitlistToggle, {
    enabled: isMobile,
  });
  useScrollIntoViewOnReveal(
    step1WaiverRef,
    currentStep === 1 && shouldShowWaiverCard && !shouldShowWaitlistToggle,
    {
      enabled: isMobile,
    },
  );

  const catalogItems = useMemo(() => checkoutCatalog?.items ?? [], [checkoutCatalog?.items]);
  const selectedCatalogItems = useMemo(
    () => getSelectedCheckoutItems(catalogItems, selectedMerch),
    [catalogItems, selectedMerch],
  );
  const actualDonation = getActualDonation();
  const merchTotal = getCheckoutItemsSubtotal(catalogItems, selectedMerch);
  const orderTotal = actualDonation + merchTotal;
  const registrationDonationFloor = getRegistrationDonationFloor(mandatoryDonation, merchTotal);
  const registrationOrderShortfall = getRegistrationOrderShortfall(mandatoryDonation, orderTotal);
  const hasMetRegistrationMinimum = isRegistrationMinimumMet(mandatoryDonation, orderTotal);
  const checkoutItemsInput = useMemo(
    () => buildCheckoutItemsInput(catalogItems, selectedMerch),
    [catalogItems, selectedMerch],
  );
  const checkoutFingerprint = JSON.stringify({
    rideId,
    actualDonation,
    checkoutItemsInput,
    shippingAddress,
    shippingAddressLine2,
    shippingCity,
    shippingState,
    shippingPostcode,
    shippingCountry,
    includePlatformFee,
  });
  const checkoutFingerprintRef = useRef(checkoutFingerprint);
  const itemsRequireShipping = selectedCheckoutItemsRequireShipping(catalogItems, selectedMerch);
  const currencyForThreshold = checkoutCatalog?.currencyCode ?? selectedCurrency;
  const addressThreshold = ADDRESS_REQUIRED_THRESHOLDS[currencyForThreshold];
  const requiresShipping =
    itemsRequireShipping || (addressThreshold != null && orderTotal > addressThreshold);

  // Resolve whether shipping country has state lookups (for conditional state requirement)
  const { data: shippingStateLookups } = trpc.publicLookups.statesByCountry.useQuery(
    { countryCode: shippingCountry },
    { enabled: requiresShipping && !!shippingCountry },
  );
  const shippingCountryHasStates = (shippingStateLookups?.length ?? 0) > 0;
  const missingShippingFields = useMemo(
    () =>
      getMissingRegistrationShippingFields({
        requiresShipping,
        streetAddress: shippingAddress,
        city: shippingCity,
        state: shippingState,
        postalCode: shippingPostcode,
        country: shippingCountry,
        stateRequired: shippingCountryHasStates,
      }),
    [
      requiresShipping,
      shippingAddress,
      shippingCity,
      shippingCountry,
      shippingCountryHasStates,
      shippingPostcode,
      shippingState,
    ],
  );
  const isShippingIncomplete = missingShippingFields.length > 0;
  const shouldShowShippingValidation = showShippingValidation && isShippingIncomplete;
  const shippingFieldLabels = useMemo<Record<RegistrationShippingField, string>>(
    () => ({
      streetAddress: tDonate('streetAddress'),
      city: tForm('city'),
      state: tForm('state'),
      postalCode: tForm('postalCode'),
      country: tForm('country'),
    }),
    [tDonate, tForm],
  );
  useScrollIntoViewOnReveal(step4ShippingRef, currentStep === 4 && shouldShowShippingValidation, {
    enabled: isMobile,
    block: 'center',
  });

  const isCheckoutLocked =
    registrationCheckoutStep === 'creating-session' ||
    registrationCheckoutStep === 'payment' ||
    registrationCheckoutStep === 'finalizing' ||
    registrationCheckoutStep === 'awaiting-settlement' ||
    registrationCheckoutStep === 'voucher-pending';

  // Get currency symbol based on selected currency
  const checkoutConfig = paymentConfigData?.paymentConfigs?.[0]
    ? {
        locale,
        clientKey: paymentConfigData.paymentConfigs[0].adyenClientKey || '',
        countryCode: toIsoCountryCode(paymentConfigData.countryCode) || 'US',
        adyenLive: paymentConfigData.paymentConfigs[0].adyenLive ?? false,
        applePay: paymentConfigData.paymentConfigs[0].adyenApplePay ?? false,
        googlePay: paymentConfigData.paymentConfigs[0].adyenGooglePay ?? false,
        ideal: paymentConfigData.paymentConfigs[0].adyenIdeal ?? false,
        bancontact: paymentConfigData.paymentConfigs[0].adyenBancontact ?? false,
        sepa: paymentConfigData.paymentConfigs[0].adyenSepa ?? false,
        twint: paymentConfigData.paymentConfigs[0].adyenTwint ?? false,
        eps: paymentConfigData.paymentConfigs[0].adyenEps ?? false,
        multibanco: paymentConfigData.paymentConfigs[0].adyenMultibanco ?? false,
        // Native PayPal is rendered separately via PayPalCheckout — disable Adyen-brokered PayPal
        paypal: false,
      }
    : null;
  const checkoutCurrencyCode =
    checkoutCatalog?.currencyCode ??
    paymentConfigData?.paymentConfigs?.[0]?.currencyCode ??
    selectedCurrency;
  const { data: donationPresetsData } = trpc.myDashboard.goalPresets.useQuery(
    { currency: checkoutCurrencyCode, presetsUsd: REGISTRATION_DONATION_PRESETS_USD },
    {
      enabled: !!authUser,
      staleTime: 5 * 60 * 1000,
    },
  );
  const donationPresetAmounts = useMemo(
    () =>
      getRegistrationDonationPresets(
        donationPresetsData?.map((preset) => preset.localAmount) ??
          REGISTRATION_DONATION_PRESETS_USD,
        registrationDonationFloor,
      ),
    [donationPresetsData, registrationDonationFloor],
  );
  const currencySymbol = useMemo(() => {
    const curr =
      currencies.find((currency) => currency.code === checkoutCurrencyCode) ||
      currencies.find((currency) => currency.code === selectedCurrency);
    return curr?.symbol || '$';
  }, [checkoutCurrencyCode, selectedCurrency]);

  // Platform fee
  const { data: feeConfig } = trpc.publicDonation.getPlatformFeeCap.useQuery(
    { currencyCode: checkoutCurrencyCode },
    { staleTime: 5 * 60 * 1000 },
  );
  const platformFeeAmount = feeConfig
    ? Math.round(Math.min(orderTotal * feeConfig.feeRate, feeConfig.capAmount) * 100) / 100
    : 0;
  const payableTotal =
    includePlatformFee && platformFeeAmount > 0 ? orderTotal + platformFeeAmount : orderTotal;

  const resetRegistrationCheckout = useCallback((options?: { clearError?: boolean }) => {
    clearInFlightPayment();
    setCheckoutSession(null);
    setRedirectSession(null);
    setCheckoutReady(false);
    setFinalizingTimedOut(false);
    setSettlementTimedOut(false);
    settlementPollingRef.current = false;
    settlementStartRef.current = 0;
    settlementPollCountRef.current = 0;
    setPaymentReceivedButIncomplete(false);
    checkoutCompletionRef.current = null;
    paypalDonationIdRef.current = null;
    setRegistrationCheckoutStep('editing');
    if (options?.clearError ?? true) {
      setSubmitError(null);
    }
  }, []);

  // Handle country change - cascading resets handled by store
  const handleCountryChange = (countryCode: string) => {
    setRideCountry(countryCode);
    setJoinWaitlist(false);
  };

  // Handle state change - cascading resets handled by store
  const handleStateChange = (stateCode: string) => {
    setRideState(stateCode);
    setJoinWaitlist(false);
  };

  // Handle ride change - store the ride id and name
  const handleRideChange = (selectedRideId: string) => {
    const ride = ridesData.find((r: RideData) => r.id === selectedRideId);
    setRide(selectedRideId, ride?.name ?? '', ride?.slug ?? '');
    setJoinWaitlist(false);
  };

  useEffect(() => {
    if (currentStep === 4 || registrationCompleteEmail) {
      return;
    }

    resetRegistrationCheckout({ clearError: false });
  }, [currentStep, registrationCompleteEmail, resetRegistrationCheckout]);

  // Auto-redirect removed — user needs time to see and click the receipt button

  const completeRegistration = useCallback(
    async (options?: {
      eventId?: string;
      registrationDonationId?: string | null;
      donationAmount?: number;
      termsAccepted?: boolean;
      newsletterOptIn?: boolean;
      triumphMarketingOptIn?: boolean;
      movemberMarketingOptIn?: boolean;
    }): Promise<boolean> => {
      const eventId = options?.eventId ?? rideId;
      if (!eventId) {
        setSubmitError(t('selectYourRide'));
        return false;
      }

      const completionKey = `${eventId}:${options?.registrationDonationId ?? 'none'}`;
      if (registrationCompletionRef.current === completionKey) {
        return true;
      }

      registrationCompletionRef.current = completionKey;
      setSubmitError(null);
      setIsSubmittingRegistration(true);

      try {
        await trpcVanilla.publicEntrant.completeRegistration.mutate({
          eventId,
          donationAmount: options?.registrationDonationId
            ? undefined
            : (options?.donationAmount ?? actualDonation),
          registrationDonationId: options?.registrationDonationId ?? undefined,
          termsAccepted: options?.termsAccepted ?? termsAcceptedForRegistration,
          newsletterOptIn: options?.newsletterOptIn ?? newsletterOptIn,
          triumphMarketingOptIn: options?.triumphMarketingOptIn ?? triumphMarketingOptIn,
          movemberMarketingOptIn: options?.movemberMarketingOptIn ?? movemberMarketingOptIn,
        });

        // Preserve donationId for receipt query before clearing checkout state
        const donationId = options?.registrationDonationId ?? checkoutSession?.donationId ?? null;
        setCompletedDonationId(donationId);
        receiptPollStartRef.current = null;

        if (returnSessionId) {
          clearAdyenRedirectSession(returnSessionId);
        }
        clearInFlightPayment();

        track('rego_completed', {
          donation_id: donationId ?? undefined,
          is_waitlist: false,
          is_redirect_return: isRegistrationRedirectReturn,
          skipped_donation:
            options?.donationAmount === 0 && !options?.registrationDonationId ? true : undefined,
        });

        setCheckoutSession(null);
        setRedirectSession(null);
        setCheckoutReady(false);
        checkoutCompletionRef.current = null;
        setIsSubmittingRegistration(false);
        setRegistrationCompleteEmail(entrant?.email ?? authUser?.email ?? null);
        reset();
        return true;
      } catch (error) {
        registrationCompletionRef.current = null;
        const message = error instanceof Error ? error.message : tCommon('error');
        track('rego_error', {
          step: 'completion',
          error_type: 'finalize_failed',
          error_message: message,
        });
        setSubmitError(message);
        setIsSubmittingRegistration(false);
        return false;
      }
    },
    [
      actualDonation,
      authUser?.email,
      checkoutSession?.donationId,
      entrant?.email,
      isRegistrationRedirectReturn,
      movemberMarketingOptIn,
      newsletterOptIn,
      reset,
      returnSessionId,
      rideId,
      t,
      tCommon,
      triumphMarketingOptIn,
      termsAcceptedForRegistration,
    ],
  );

  const revealShippingValidation = useCallback(() => {
    setShowShippingValidation(true);
    window.requestAnimationFrame(() => {
      step4ShippingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, []);

  const revealPaymentSection = useCallback(() => {
    window.requestAnimationFrame(() => {
      step4PaymentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, []);

  const handleStartCheckout = async () => {
    if (!rideId) {
      setSubmitError(t('selectYourRide'));
      setIsSubmittingRegistration(false);
      return;
    }

    const donorEmail = entrant?.email ?? authUser?.email;
    if (!donorEmail) {
      setSubmitError(tCommon('error'));
      setIsSubmittingRegistration(false);
      return;
    }

    if (isShippingIncomplete) {
      revealShippingValidation();
      setIsSubmittingRegistration(false);
      return;
    }

    if (!checkoutConfig || !paymentConfigData?.paymentConfigs?.length) {
      setSubmitError(
        isPaymentConfigLoading || isPaymentConfigFetching ? tCommon('loading') : tCommon('error'),
      );
      setIsSubmittingRegistration(false);
      return;
    }

    setSubmitError(null);
    setShowShippingValidation(false);
    revealPaymentSection();
    setIsSubmittingRegistration(true);
    setRegistrationCheckoutStep('creating-session');

    try {
      const returnUrl = window.location.origin + window.location.pathname;
      const registrationCompletion = buildRegistrationCompletionState({
        eventId: rideId,
        termsAccepted: termsAcceptedForRegistration,
        newsletterOptIn,
        triumphMarketingOptIn,
        movemberMarketingOptIn,
      });
      const donorAddress = requiresShipping
        ? {
            streetAddress: shippingAddress,
            ...(shippingAddressLine2 ? { addressLine2: shippingAddressLine2 } : {}),
            city: shippingCity,
            ...(shippingState ? { state: shippingState } : {}),
            postalCode: shippingPostcode,
            country: shippingCountry,
          }
        : undefined;

      const donorFirstName = entrant?.firstName ?? authUser?.name?.split(' ')[0] ?? '';
      const donorLastName =
        entrant?.lastName ?? authUser?.name?.split(' ').slice(1).join(' ') ?? '';

      const session = await createCheckoutSession.mutateAsync({
        amount: payableTotal,
        donorEmail,
        donorFirstName,
        donorLastName,
        isAnonymous: false,
        returnUrl,
        donorCountryCode:
          toIsoCountryCode(shippingCountry) ||
          toIsoCountryCode(paymentConfigData.countryCode) ||
          undefined,
        donorAddress,
        items: checkoutItemsInput,
        scope: { scope: 'registration', eventId: rideId },
        platformFee: includePlatformFee ? platformFeeAmount : 0,
      });

      checkoutCompletionRef.current = registrationCompletion;

      saveAdyenRedirectSession({
        donationId: session.donationId,
        sessionId: session.sessionId,
        sessionData: session.sessionData,
        donorEmail,
        flow: 'registration',
        amount: payableTotal,
        currencySymbol,
        commitInputs: {
          donorFirstName,
          donorLastName,
          isAnonymous: false,
          giftAidEligible: false,
          returnUrl,
          donorCountryCode:
            toIsoCountryCode(shippingCountry) ||
            toIsoCountryCode(paymentConfigData.countryCode) ||
            undefined,
          donorAddress,
          scope: { scope: 'registration', eventId: rideId },
          items: checkoutItemsInput,
          platformFee: includePlatformFee ? platformFeeAmount : 0,
        },
        registrationCompletion,
      });

      saveInFlightPayment(session.donationId, session.sessionId);

      checkoutFingerprintRef.current = checkoutFingerprint;
      setCheckoutReady(false);
      setCheckoutSession(session);
      setRegistrationCheckoutStep('payment');
      track('rego_checkout_initiated', {
        donation_amount: payableTotal,
        has_merch: Object.keys(selectedMerch).length > 0,
        currency: checkoutCurrencyCode,
      });
      trackDonation(
        'donation_checkout_initiated',
        {
          amount: payableTotal,
          total_charge: payableTotal,
          currency: checkoutCurrencyCode,
          donation_id: session.donationId,
          has_paypal: !!session.paypalAvailable,
        },
        { scope: 'registration', currency: checkoutCurrencyCode },
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : tCommon('error');
      track('rego_error', {
        step: 'checkout',
        error_type: 'session_creation_failed',
        error_message: message,
      });
      trackDonation(
        'donation_error',
        { step: 'checkout', error_type: 'session_creation_failed', error_message: message },
        { scope: 'registration' },
      );
      resetRegistrationCheckout({ clearError: false });
      setSubmitError(message);
    } finally {
      setIsSubmittingRegistration(false);
    }
  };

  const handleBeforeSubmit = useCallback(
    async (paymentMethodType: string) => {
      if (!checkoutSession) {
        throw new Error('No checkout session');
      }

      await commitCheckoutMutationRef.current({
        amount: payableTotal,
        donorEmail: entrant?.email ?? authUser?.email ?? '',
        donorFirstName: entrant?.firstName ?? authUser?.name?.split(' ')[0] ?? '',
        donorLastName: entrant?.lastName ?? authUser?.name?.split(' ').slice(1).join(' ') ?? '',
        isAnonymous: false,
        returnUrl: window.location.origin + window.location.pathname,
        donorCountryCode:
          toIsoCountryCode(shippingCountry) ||
          toIsoCountryCode(paymentConfigData?.countryCode) ||
          undefined,
        donorAddress: requiresShipping
          ? {
              streetAddress: shippingAddress,
              ...(shippingAddressLine2 ? { addressLine2: shippingAddressLine2 } : {}),
              city: shippingCity,
              ...(shippingState ? { state: shippingState } : {}),
              postalCode: shippingPostcode,
              country: shippingCountry,
            }
          : undefined,
        items: checkoutItemsInput,
        scope: { scope: 'registration', eventId: rideId },
        donationId: checkoutSession.donationId,
        adyenSessionId: checkoutSession.sessionId,
        adyenSessionData: checkoutSession.sessionData,
        adyenMethodType: paymentMethodType,
        platformFee: includePlatformFee ? platformFeeAmount : 0,
        registrationCompletion: checkoutCompletionRef.current ?? undefined,
      });
    },
    [
      authUser?.email,
      authUser?.name,
      checkoutItemsInput,
      checkoutSession,
      entrant?.email,
      entrant?.firstName,
      entrant?.lastName,
      includePlatformFee,
      payableTotal,
      platformFeeAmount,
      paymentConfigData?.countryCode,
      requiresShipping,
      rideId,
      shippingAddress,
      shippingAddressLine2,
      shippingCity,
      shippingCountry,
      shippingPostcode,
      shippingState,
    ],
  );

  const handlePaymentCompleted = useCallback(
    async (
      data: PaymentCompletedData,
      options?: {
        registrationDonationId?: string | null;
        completion?: NonNullable<typeof registrationRedirectStorage>['registrationCompletion'];
        isRedirectReturn?: boolean;
      },
    ) => {
      const completion = options?.completion ?? checkoutCompletionRef.current ?? undefined;

      // Voucher payments (Multibanco): user must pay offline, webhook completes registration
      if (data.resultCode === 'PresentToShopper') {
        const donationId =
          options?.registrationDonationId ?? checkoutSession?.donationId ?? undefined;
        track('rego_voucher_presented', {
          donation_id: donationId,
          payment_method: (data as PaymentCompletedData & { paymentMethod?: { type?: string } })
            .paymentMethod?.type,
        });
        setRegistrationCheckoutStep('voucher-pending');
        return;
      }

      if (
        data.resultCode === 'Authorised' ||
        data.resultCode === 'Received' ||
        data.resultCode === 'Pending'
      ) {
        const donationId =
          options?.registrationDonationId ?? checkoutSession?.donationId ?? undefined;
        const paymentMethod = (data as PaymentCompletedData & { paymentMethod?: { type?: string } })
          .paymentMethod?.type;
        const eventSource = options?.isRedirectReturn ? 'redirect_finalized' : 'checkout';
        track('rego_payment_completed', {
          result_code: data.resultCode,
          donation_id: donationId,
          payment_method: paymentMethod,
          amount: payableTotal || undefined,
          currency: checkoutCurrencyCode || undefined,
          is_redirect_return: options?.isRedirectReturn ?? false,
          event_source: eventSource,
        });
        trackDonation(
          'donation_payment_completed',
          {
            result_code: data.resultCode,
            donation_id: donationId,
            payment_method: paymentMethod ?? 'unknown',
            event_source: eventSource,
          },
          { scope: 'registration', currency: checkoutCurrencyCode },
        );
        trackDonation(
          'donation_completed',
          {
            donation_id: donationId,
            amount: payableTotal,
            currency: checkoutCurrencyCode,
            event_source: eventSource,
          },
          { scope: 'registration', currency: checkoutCurrencyCode },
        );
        setRegistrationCheckoutStep('finalizing');
        const completed = await completeRegistration({
          eventId: completion?.eventId ?? rideId,
          registrationDonationId: options?.registrationDonationId ?? checkoutSession?.donationId,
          termsAccepted: completion?.termsAccepted,
          newsletterOptIn: completion?.newsletterOptIn,
          triumphMarketingOptIn: completion?.triumphMarketingOptIn,
          movemberMarketingOptIn: completion?.movemberMarketingOptIn,
        });
        if (!completed) {
          setRegistrationCheckoutStep('failed');
        }
        return;
      }

      setSubmitError(tDonate('form.paymentFailedMessage'));
      resetRegistrationCheckout({ clearError: false });
      setRegistrationCheckoutStep('failed');
    },
    [
      checkoutCurrencyCode,
      checkoutSession?.donationId,
      completeRegistration,
      payableTotal,
      resetRegistrationCheckout,
      rideId,
      tDonate,
    ],
  );

  const handlePaymentFailed = useCallback(
    async (data: PaymentFailedData | undefined, donationId?: string | null) => {
      if (donationId) {
        await markRedirectFailedMutationRef.current({
          donationId,
          reason: data?.resultCode
            ? `Registration checkout failed: ${data.resultCode}`
            : 'Registration checkout failed',
        });
      }

      track('rego_error', {
        step: 'payment',
        error_type: 'payment_failed',
        error_message: data?.resultCode ?? 'unknown',
      });
      trackDonation(
        'donation_payment_failed',
        {
          result_code: data?.resultCode ?? undefined,
          donation_id: donationId ?? undefined,
          payment_method: undefined,
        },
        { scope: 'registration' },
      );
      track('rego_payment_retry', {
        previous_error: data?.resultCode ?? undefined,
      });

      setSubmitError(tDonate('form.paymentFailedMessage'));
      resetRegistrationCheckout({ clearError: false });
      setRegistrationCheckoutStep('failed');
    },
    [resetRegistrationCheckout, tDonate],
  );

  const handleCheckoutReady = useCallback(() => {
    setCheckoutReady(true);
  }, []);

  const handleCheckoutError = useCallback(() => {
    track('rego_error', {
      step: 'checkout',
      error_type: 'checkout_init_failed',
      error_message: 'Adyen checkout component failed to initialize',
    });
    trackDonation(
      'donation_error',
      {
        step: 'checkout',
        error_type: 'checkout_init_failed',
        error_message: 'Adyen checkout component failed to initialize',
      },
      { scope: 'registration' },
    );
    resetRegistrationCheckout({ clearError: false });
    setRegistrationCheckoutStep('failed');
    setSubmitError(tDonate('form.errorPaymentInitFailed'));
  }, [resetRegistrationCheckout, tDonate]);

  const handleCheckoutPaymentCompleted = useCallback(
    (data: PaymentCompletedData) => {
      void handlePaymentCompleted(data);
    },
    [handlePaymentCompleted],
  );

  const handleCheckoutPaymentFailed = useCallback(
    (data: PaymentFailedData | undefined) => {
      void handlePaymentFailed(data);
    },
    [handlePaymentFailed],
  );

  const handlePayPalPaymentCompleted = useCallback(async () => {
    const donationId = paypalDonationIdRef.current ?? undefined;
    track('rego_payment_completed', {
      result_code: 'Authorised',
      donation_id: donationId,
      payment_method: 'paypal',
      amount: payableTotal || undefined,
      currency: checkoutCurrencyCode || undefined,
      event_source: 'paypal',
    });
    trackDonation(
      'donation_payment_completed',
      {
        result_code: 'Authorised',
        donation_id: donationId,
        payment_method: 'paypal',
        event_source: 'paypal',
      },
      { scope: 'registration', currency: checkoutCurrencyCode },
    );
    trackDonation(
      'donation_completed',
      {
        donation_id: donationId,
        amount: payableTotal,
        currency: checkoutCurrencyCode,
        event_source: 'paypal',
      },
      { scope: 'registration', currency: checkoutCurrencyCode },
    );
    setRegistrationCheckoutStep('finalizing');
    const completed = await completeRegistration({
      eventId: checkoutCompletionRef.current?.eventId ?? rideId,
      registrationDonationId: donationId,
      termsAccepted: checkoutCompletionRef.current?.termsAccepted,
      newsletterOptIn: checkoutCompletionRef.current?.newsletterOptIn,
      triumphMarketingOptIn: checkoutCompletionRef.current?.triumphMarketingOptIn,
      movemberMarketingOptIn: checkoutCompletionRef.current?.movemberMarketingOptIn,
    });
    if (!completed) {
      setRegistrationCheckoutStep('failed');
    }
  }, [checkoutCurrencyCode, completeRegistration, payableTotal, rideId]);

  const handlePayPalPaymentFailed = useCallback(
    (message: string, donationId?: string) => {
      track('rego_error', {
        step: 'payment',
        error_type: 'paypal_failed',
        error_message: message,
      });
      trackDonation(
        'donation_payment_failed',
        { result_code: 'paypal_failed', payment_method: 'paypal' },
        { scope: 'registration' },
      );
      if (donationId) {
        void markRedirectFailedMutationRef.current({ donationId, reason: `PayPal: ${message}` });
      }
      resetRegistrationCheckout({ clearError: false });
      setSubmitError(message);
    },
    [resetRegistrationCheckout],
  );

  const handleRedirectPaymentCompleted = useCallback(
    (data: PaymentCompletedData) => {
      const serverCompletion = redirectContext?.registrationCompletion as
        | RegistrationCompletionState
        | undefined;
      void handlePaymentCompleted(data, {
        registrationDonationId: redirectSession?.donationId,
        completion:
          serverCompletion ?? registrationRedirectStorage?.registrationCompletion ?? undefined,
        isRedirectReturn: true,
      });
    },
    [
      handlePaymentCompleted,
      redirectContext?.registrationCompletion,
      redirectSession?.donationId,
      registrationRedirectStorage?.registrationCompletion,
    ],
  );

  const handleRedirectPaymentFailed = useCallback(
    (data: PaymentFailedData | undefined) => {
      void handlePaymentFailed(data, redirectSession?.donationId);
    },
    [handlePaymentFailed, redirectSession?.donationId],
  );

  useEffect(() => {
    if (!isRegistrationRedirectReturn) {
      setRedirectSession(null);
      return;
    }

    setCurrentStep(4);
    setRegistrationCheckoutStep('finalizing');
  }, [isRegistrationRedirectReturn, setCurrentStep]);

  useEffect(() => {
    if (!isRegistrationRedirectReturn || !isRedirectContextError) {
      return;
    }

    track('rego_redirect_failed', {
      donation_id: returnDonationId ?? undefined,
      reason: 'redirect_context_load_failed',
    });
    trackDonation(
      'donation_redirect_failed',
      {
        donation_id: returnDonationId ?? undefined,
        reason: 'redirect_context_load_failed',
      },
      { scope: 'registration' },
    );
    setSubmitError(tCommon('error'));
    resetRegistrationCheckout({ clearError: false });
    setRegistrationCheckoutStep('failed');
  }, [
    isRedirectContextError,
    isRegistrationRedirectReturn,
    resetRegistrationCheckout,
    returnDonationId,
    tCommon,
  ]);

  useEffect(() => {
    if (
      !isRegistrationRedirectReturn ||
      !returnDonationId ||
      !redirectResult ||
      !redirectContext ||
      redirectSession
    ) {
      return;
    }

    // Wait for session recovery before resolving redirect
    if (returnRecoveryToken && !recovery.attempted) return;

    const resolution = resolveRegistrationRedirectFlow({
      returnDonationId,
      redirectResult,
      returnEventId: returnEventId || null,
      returnSessionId: returnSessionId || null,
      redirectContext,
      serverCompletion:
        (redirectContext.registrationCompletion as RegistrationCompletionState) ?? null,
      storedSession: registrationRedirectStorage
        ? {
            sessionData: registrationRedirectStorage.sessionData,
            registrationCompletion: registrationRedirectStorage.registrationCompletion,
          }
        : null,
    });

    track('rego_redirect_resumed', {
      donation_id: returnDonationId,
      resolution_kind: resolution.kind,
      has_stored_session: !!registrationRedirectStorage,
      webhook_settled: resolution.kind === 'complete-registration',
    });
    trackDonation(
      'donation_redirect_resumed',
      {
        donation_id: returnDonationId,
        webhook_settled: resolution.kind === 'complete-registration',
        has_stored_session: !!registrationRedirectStorage,
      },
      { scope: 'registration' },
    );

    switch (resolution.kind) {
      case 'complete-registration':
        void completeRegistration(resolution.completion).then((completed) => {
          if (completed && settlementPollingRef.current) {
            // Polling detected paid → completeRegistration succeeded
            track('rego_settlement_resolved', {
              donation_id: returnDonationId,
              outcome: 'completed',
              poll_duration_ms: Date.now() - settlementStartRef.current,
              poll_count: settlementPollCountRef.current,
              settled_status: 'paid',
            });
          } else if (!completed) {
            if (settlementPollingRef.current) {
              // Payment confirmed by webhook but completeRegistration failed (likely not authenticated)
              // The webhook safety net handles registration — show friendly message
              track('rego_settlement_resolved', {
                donation_id: returnDonationId,
                outcome: 'payment_received_unauthenticated',
                poll_duration_ms: Date.now() - settlementStartRef.current,
                poll_count: settlementPollCountRef.current,
                settled_status: 'paid',
              });
              setPaymentReceivedButIncomplete(true);
              setRegistrationCheckoutStep('failed');
            } else {
              track('rego_redirect_failed', {
                donation_id: returnDonationId,
                reason: 'registration_completion_failed',
              });
              trackDonation(
                'donation_redirect_failed',
                { donation_id: returnDonationId, reason: 'registration_completion_failed' },
                { scope: 'registration' },
              );
              setRegistrationCheckoutStep('failed');
            }
          }
        });
        break;
      case 'finalize-payment':
        setRedirectSession(resolution.session);
        setRegistrationCheckoutStep('finalizing');
        break;
      case 'failed': {
        // Check if donation is still pending (no session data to finalize SDK) vs definitively failed
        const isStillPending = !(SETTLED_DONATION_STATUSES as readonly string[]).includes(
          redirectContext.status,
        );
        if (isStillPending) {
          // Payment may still be processing (iDEAL/Multibanco) — poll for webhook settlement
          if (!settlementPollingRef.current) {
            settlementPollingRef.current = true;
            settlementStartRef.current = Date.now();
            settlementPollCountRef.current = 0;
            track('rego_awaiting_settlement', {
              donation_id: returnDonationId,
            });
            setRegistrationCheckoutStep('awaiting-settlement');
          } else {
            // Subsequent poll tick while still pending
            settlementPollCountRef.current += 1;
          }
        } else if (settlementPollingRef.current) {
          // Was polling, now definitively failed/cancelled
          const pollDuration = Date.now() - settlementStartRef.current;
          track('rego_settlement_resolved', {
            donation_id: returnDonationId,
            outcome: redirectContext.status === 'cancelled' ? 'cancelled' : 'failed',
            poll_duration_ms: pollDuration,
            poll_count: settlementPollCountRef.current,
            settled_status: redirectContext.status,
          });
          setSubmitError(tDonate('form.paymentFailedMessage'));
          resetRegistrationCheckout({ clearError: false });
          setRegistrationCheckoutStep('failed');
        } else {
          // Definitively failed/cancelled on first check (no polling)
          track('rego_redirect_failed', {
            donation_id: returnDonationId,
            reason: `payment_${redirectContext.status}`,
          });
          trackDonation(
            'donation_redirect_failed',
            { donation_id: returnDonationId, reason: `payment_${redirectContext.status}` },
            { scope: 'registration' },
          );
          setSubmitError(tDonate('form.paymentFailedMessage'));
          resetRegistrationCheckout({ clearError: false });
          setRegistrationCheckoutStep('failed');
        }
        break;
      }
    }
  }, [
    completeRegistration,
    isRegistrationRedirectReturn,
    recovery.attempted,
    returnRecoveryToken,
    resetRegistrationCheckout,
    redirectContext,
    redirectResult,
    redirectSession,
    registrationRedirectStorage,
    returnDonationId,
    returnEventId,
    returnSessionId,
    tDonate,
  ]);

  // localStorage recovery — native app 3DS return with no URL params.
  // Bypasses the main resolution (which requires redirectResult) and checks
  // redirectContext directly. Auto-completes registration if webhook settled.
  const localStorageRecoveryRef = useRef(false);
  useEffect(() => {
    if (!isLocalStorageRecovery || !returnDonationId || !redirectContext) return;
    if (localStorageRecoveryRef.current) return;
    localStorageRecoveryRef.current = true;

    const status = redirectContext.status;
    const isSettled = (SETTLED_DONATION_STATUSES as readonly string[]).includes(status);

    queueMicrotask(() => {
      track('rego_redirect_resumed', {
        donation_id: returnDonationId,
        resolution_kind: 'localStorage_recovery',
        has_stored_session: false,
        webhook_settled: isSettled,
      });

      if (status === 'paid') {
        // Webhook settled — auto-complete registration silently
        const serverCompletion = redirectContext.registrationCompletion as
          | { eventId: string; termsAccepted: boolean }
          | undefined;
        void completeRegistration({
          eventId: serverCompletion?.eventId ?? returnEventId ?? undefined,
          registrationDonationId: returnDonationId,
        });
      } else if (isSettled) {
        clearInFlightPayment();
        setSubmitError(tDonate('form.paymentFailedMessage'));
        resetRegistrationCheckout({ clearError: false });
        setRegistrationCheckoutStep('failed');
      } else {
        // Still pending — poll for webhook settlement
        setRegistrationCheckoutStep('awaiting-settlement');
      }
    });
  }, [
    isLocalStorageRecovery,
    returnDonationId,
    redirectContext,
    returnEventId,
    completeRegistration,
    resetRegistrationCheckout,
    tDonate,
  ]);

  // localStorage recovery — react to redirectContext updates during settlement polling
  useEffect(() => {
    if (
      !isLocalStorageRecovery ||
      registrationCheckoutStep !== 'awaiting-settlement' ||
      !redirectContext
    )
      return;

    const status = redirectContext.status;
    if (!(SETTLED_DONATION_STATUSES as readonly string[]).includes(status)) return;

    if (status === 'paid') {
      const serverCompletion = redirectContext.registrationCompletion as
        | { eventId: string; termsAccepted: boolean }
        | undefined;
      void completeRegistration({
        eventId: serverCompletion?.eventId ?? returnEventId ?? undefined,
        registrationDonationId: returnDonationId ?? undefined,
      });
    } else {
      clearInFlightPayment();
      setSubmitError(tDonate('form.paymentFailedMessage'));
      resetRegistrationCheckout({ clearError: false });
      setRegistrationCheckoutStep('failed');
    }
  }, [
    isLocalStorageRecovery,
    registrationCheckoutStep,
    redirectContext,
    returnDonationId,
    returnEventId,
    completeRegistration,
    resetRegistrationCheckout,
    tDonate,
  ]);

  useEffect(() => {
    if (isRegistrationRedirectReturn && !redirectContext && !redirectSession) {
      return;
    }

    if (registrationCheckoutStep !== 'finalizing') {
      queueMicrotask(() => setFinalizingTimedOut(false));
      return;
    }

    const timer = window.setTimeout(
      () => setFinalizingTimedOut(true),
      REGISTRATION_FINALIZE_TIMEOUT_MS,
    );
    return () => window.clearTimeout(timer);
  }, [isRegistrationRedirectReturn, redirectContext, redirectSession, registrationCheckoutStep]);

  // Settlement polling timeout — 60s warning
  useEffect(() => {
    if (registrationCheckoutStep !== 'awaiting-settlement') {
      queueMicrotask(() => setSettlementTimedOut(false));
      return;
    }

    const timer = window.setTimeout(() => {
      track('rego_settlement_timeout', {
        donation_id: returnDonationId ?? undefined,
        poll_duration_ms: Date.now() - settlementStartRef.current,
        poll_count: settlementPollCountRef.current,
      });
      setSettlementTimedOut(true);
    }, SETTLEMENT_POLL_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [registrationCheckoutStep, returnDonationId]);

  const retryRegistrationCompletion = useCallback(async () => {
    const registrationDonationId = redirectSession?.donationId ?? checkoutSession?.donationId;
    const serverCompletion = redirectContext?.registrationCompletion as
      | RegistrationCompletionState
      | undefined;
    const completion =
      serverCompletion ?? registrationRedirectStorage?.registrationCompletion ?? undefined;
    const eventId = completion?.eventId ?? rideId ?? returnEventId ?? undefined;

    if (!registrationDonationId || !eventId) {
      resetRegistrationCheckout();
      return;
    }

    setSubmitError(null);
    setRegistrationCheckoutStep('finalizing');

    const completed = await completeRegistration({
      eventId,
      registrationDonationId,
      termsAccepted: completion?.termsAccepted ?? termsAcceptedForRegistration,
      newsletterOptIn: completion?.newsletterOptIn ?? newsletterOptIn,
      triumphMarketingOptIn: completion?.triumphMarketingOptIn ?? triumphMarketingOptIn,
      movemberMarketingOptIn: completion?.movemberMarketingOptIn ?? movemberMarketingOptIn,
    });

    if (!completed) {
      setRegistrationCheckoutStep('failed');
    }
  }, [
    checkoutSession?.donationId,
    completeRegistration,
    movemberMarketingOptIn,
    redirectContext?.registrationCompletion,
    redirectSession?.donationId,
    registrationRedirectStorage?.registrationCompletion,
    resetRegistrationCheckout,
    returnEventId,
    rideId,
    termsAcceptedForRegistration,
    newsletterOptIn,
    triumphMarketingOptIn,
  ]);

  useEffect(() => {
    if (!checkoutSession) {
      checkoutFingerprintRef.current = checkoutFingerprint;
      return;
    }

    if (checkoutFingerprintRef.current === checkoutFingerprint) {
      return;
    }

    resetRegistrationCheckout();
    // Checkout session must match the latest cart and shipping details.
  }, [checkoutFingerprint, checkoutSession, resetRegistrationCheckout]);

  const handleSubmit = async () => {
    setSubmitError(null);

    if (!rideId) {
      setSubmitError(t('selectYourRide'));
      return;
    }

    if (isShippingIncomplete) {
      revealShippingValidation();
      return;
    }

    setIsSubmittingRegistration(true);

    try {
      if (shouldSkipPaymentStep) {
        await trpcVanilla.publicEntrant.ensureRegistration.mutate({
          eventId: rideId,
          joinWaitlist: true,
          defaultBlog: {
            title: t('defaultBlogTitle'),
            content: t('defaultBlogContent'),
          },
        });

        // Persist T&C and notification preferences for waitlist registrations
        await trpcVanilla.publicEntrant.completeRegistration.mutate({
          eventId: rideId,
          termsAccepted: termsAcceptedForRegistration,
          newsletterOptIn,
          triumphMarketingOptIn,
          movemberMarketingOptIn,
        });

        setIsSubmittingRegistration(false);
        // Hard navigation: session was established mid-flow (step 2), so the
        // SSR-seeded SessionProvider still has session: null from initial load.
        // Full page load ensures root layout re-runs with the new auth cookie.
        window.location.href = '/my/dashboard';
        return;
      }

      if (requiresShipping) {
        track('rego_shipping_submitted', {
          country: shippingCountry,
          has_state: !!shippingState,
        });
      }

      if (orderTotal > 0) {
        await handleStartCheckout();
        return;
      }

      const completed = await completeRegistration();
      if (!completed) {
        return;
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : tCommon('error');
      setSubmitError(message);
      setIsSubmittingRegistration(false);
      return;
    }
  };

  const handleSkipAndComplete = useCallback(async () => {
    clearCart();
    await completeRegistration({ donationAmount: 0 });
  }, [clearCart, completeRegistration]);

  const handleSkipAuthContinue = async () => {
    setSubmitError(null);

    if (!rideId) {
      setSubmitError(t('selectYourRide'));
      return;
    }

    setIsSubmittingRegistration(true);
    try {
      await trpcVanilla.publicEntrant.ensureRegistration.mutate({
        eventId: rideId,
        joinWaitlist,
        defaultBlog: {
          title: t('defaultBlogTitle'),
          content: t('defaultBlogContent'),
        },
      });
      setCurrentStep(3);
    } catch (error) {
      const message = error instanceof Error ? error.message : tCommon('error');
      setSubmitError(message);
    } finally {
      setIsSubmittingRegistration(false);
    }
  };

  const utils = trpc.useUtils();

  const handleAuthStepSuccess = async () => {
    router.refresh();
    // Invalidate cached auth state so header updates to logged-in state
    await utils.authMe.me.invalidate();

    try {
      const guard = await trpcVanilla.publicEntrant.getRegistrationGuard.query();
      if (guard.blocked) {
        // Keep the user in step 2 and show the blocked CTAs inline instead of
        // bouncing them back to ride selection.
        registrationAuth.setEmailStatus('existing-registered');
        setCurrentStep(2);
        return;
      }
      if (guard.pendingRegistration) {
        setRideCountryCode(guard.pendingRegistration.countryCode);
        setRideStateCode(guard.pendingRegistration.stateCode ?? '');
        setRideId(guard.pendingRegistration.eventId);
        setRideName(guard.pendingRegistration.eventName ?? '');
        updateRideUrl(
          guard.pendingRegistration.countryCode,
          guard.pendingRegistration.stateCode ?? '',
          '',
        );
      }
      if (guard.pendingRegistration?.checkoutState && !hasHydratedCheckout.current) {
        hasHydratedCheckout.current = true;
        hydrateFromServer(guard.pendingRegistration.checkoutState);
      }
    } catch {
      // Guard check failed — proceed to step 3
    }

    setCurrentStep(3);
  };

  // Debounced save of checkout state (steps 3-4) to server
  const saveCheckoutStateMutation = trpc.publicEntrant.saveCheckoutState.useMutation();
  const saveCheckoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!authUser || currentStep < 3 || registrationCompleteEmail) {
      return;
    }

    if (saveCheckoutTimerRef.current) {
      clearTimeout(saveCheckoutTimerRef.current);
    }

    saveCheckoutTimerRef.current = setTimeout(() => {
      const snapshot = getCheckoutStateSnapshot();
      const checkoutState = {
        ...snapshot,
        lastReachedStep: currentStep === 4 ? 4 : 3,
      };
      saveCheckoutStateMutation.mutate(
        { eventId: rideId || undefined, checkoutState },
        {
          onError: (err) => {
            track('rego_error', {
              step: 'checkout',
              error_type: 'save_state_failed',
              ...describeSaveError(err),
            });
          },
        },
      );
    }, 500);

    return () => {
      if (saveCheckoutTimerRef.current) {
        clearTimeout(saveCheckoutTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    authUser,
    currentStep,
    registrationCompleteEmail,
    newsletterOptIn,
    triumphMarketingOptIn,
    movemberMarketingOptIn,
    donationAmount,
    customDonation,
    selectedMerch,
    shippingAddress,
    shippingAddressLine2,
    shippingCity,
    shippingState,
    shippingPostcode,
    shippingCountry,
  ]);

  useEffect(() => {
    if (currentStep !== 4) {
      setShowShippingValidation(false);
      return;
    }

    if (!isShippingIncomplete && showShippingValidation) {
      setShowShippingValidation(false);
    }
  }, [currentStep, isShippingIncomplete, showShippingValidation]);

  // Step validation
  // For step 1: need country, ride selected, waiver accepted
  // If country has states, also need state selected
  // If ride is full, must join waitlist
  const isStep1Valid =
    rideCountryCode &&
    rideId &&
    waiverAccepted &&
    (hasStates ? rideStateCode : true) &&
    (isSelectedCityFull ? joinWaitlist : true) &&
    // Block progression entirely if the active campaign for this ride is cancelled.
    !isSelectedRideCancelled;
  // Step 2 validation: AuthStep handles its own validation via RHF+Zod
  // The step is "valid" once auth succeeds (entrant exists in store)
  // Navigation is handled by AuthStep's onSuccess callback, not the Continue button
  const isStep2Valid = !!entrant;
  // Step 3 is preferences only (newsletter opt-ins) — always valid.
  // Once the user leaves step 1, terms are treated as accepted for completion.
  const isStep3Valid = true;
  const isStep4Valid =
    termsAcceptedForRegistration &&
    // If mandatory donation, the full payable order must meet the minimum
    (!hasMandatoryDonation || hasMetRegistrationMinimum) &&
    // If selected items require shipping, must have shipping address (+ state when country has states)
    (!requiresShipping ||
      (shippingAddress &&
        shippingCity &&
        shippingPostcode &&
        shippingCountry &&
        (!shippingCountryHasStates || shippingState))) &&
    // The payment step itself can create a checkout session once cart details are valid
    orderTotal >= 0;
  const canSubmitStep4 =
    termsAcceptedForRegistration &&
    (!hasMandatoryDonation || hasMetRegistrationMinimum) &&
    orderTotal >= 0;
  const isCheckoutCreatingSession = registrationCheckoutStep === 'creating-session';
  const isCheckoutPaymentActive = registrationCheckoutStep === 'payment';
  const paypalAvailable = !!checkoutSession?.paypalAvailable && !!checkoutSession?.paypalClientId;
  const isCheckoutFinalizing = registrationCheckoutStep === 'finalizing';
  const isAwaitingSettlement = registrationCheckoutStep === 'awaiting-settlement';
  const isVoucherPending = registrationCheckoutStep === 'voucher-pending';
  const isCheckoutFailed = registrationCheckoutStep === 'failed';
  const shouldHideStepNavigation =
    isCheckoutCreatingSession ||
    isCheckoutPaymentActive ||
    isCheckoutFinalizing ||
    isAwaitingSettlement ||
    isVoucherPending ||
    isCheckoutFailed;

  const canProceed =
    !isCampaignClosed &&
    ((currentStep === 1 && isStep1Valid) ||
      (currentStep === 2 && isStep2Valid) ||
      (currentStep === 3 && isStep3Valid) ||
      (currentStep === 4 && isStep4Valid));
  useScrollIntoViewOnReveal(
    step4PaymentRef,
    currentStep === 4 && (isCheckoutCreatingSession || isCheckoutPaymentActive),
    {
      enabled: isMobile,
      block: 'center',
    },
  );
  const currentStepLabel =
    currentStep === 1
      ? t('chooseYourRide')
      : currentStep === 2
        ? t('yourDetails')
        : currentStep === 3
          ? t('terms')
          : t('payment');
  const handleChangeRideShortcut = useCallback(() => {
    setCurrentStep(1);
  }, []);

  // Show loading skeleton while auth guard is resolving
  if (init.isLoading) {
    return (
      <div className="min-h-dvh bg-stone-950 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-dgr-gold" />
      </div>
    );
  }

  return (
    <div
      className="notranslate min-h-dvh overflow-x-clip bg-stone-950 lg:h-dvh lg:flex lg:flex-col lg:overflow-hidden"
      translate="no"
    >
      <div className="lg:flex-1 lg:flex lg:min-h-0">
        {/* Left Side - Hero Image (fixed) */}
        <div className="hidden lg:block lg:w-1/2 xl:w-[55%] relative flex-shrink-0">
          <Image
            src="/images/dgr/HeroImage.jpg"
            alt="Distinguished gentlemen on classic motorcycles"
            fill
            className="object-cover"
            priority
          />
          <div className="absolute inset-0 bg-gradient-to-r from-stone-950/30 via-transparent to-stone-950" />

          {/* Overlay Content */}
          <div className="absolute bottom-0 left-0 right-0 p-12 bg-gradient-to-t from-stone-950 via-stone-950/80 to-transparent">
            <div className="max-w-lg">
              <p className="text-dgr-gold font-medium tracking-wide uppercase text-sm mb-3">
                Sunday, May 17th 2026
              </p>
              <h2 className="text-4xl font-bold text-white mb-4 leading-tight">
                {t('joinRidersWorldwide')}
              </h2>
              <p className="text-stone-300 text-lg">{t('bePartOfEvent')}</p>

              {/* Stats */}
              <div className="flex gap-8 mt-8">
                <div>
                  <p className="text-3xl font-bold text-dgr-gold">$60M+</p>
                  <p className="text-stone-400 text-sm">{t('raisedToDate')}</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-dgr-gold">110</p>
                  <p className="text-stone-400 text-sm">{t('countries')}</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-dgr-gold">1000+</p>
                  <p className="text-stone-400 text-sm">{t('cities')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Form (scrollable) */}
        <div
          ref={formPanelRef}
          className="flex w-full flex-col overflow-x-clip bg-stone-950 lg:w-1/2 lg:overflow-y-auto lg:pt-20 xl:w-[45%]"
        >
          {/* Mobile Hero - Enhanced */}
          <div className="relative h-40 lg:hidden sm:h-44">
            <Image
              src="/images/dgr/HeroImage.jpg"
              alt="Distinguished gentlemen on classic motorcycles"
              fill
              className="object-cover object-top"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-b from-stone-950/60 via-stone-950/40 to-stone-950" />

            {/* Mobile Header Content */}
            <div className="absolute inset-x-0 bottom-0 p-4 pt-12 sm:p-6 sm:pt-16">
              <p className="text-dgr-gold font-medium tracking-wide uppercase text-xs mb-1">
                May 17th, 2026
              </p>
              <h1 className="text-xl font-bold text-white sm:text-2xl">{t('title')}</h1>
              <p className="text-stone-300 text-sm mt-1">{t('joinRidersWorldwide')}</p>
            </div>
          </div>

          {/* Progress Steps — hidden on completion screen */}
          <StepIndicator
            currentStep={currentStep}
            stepLabel={currentStepLabel}
            hidden={!!registrationCompleteEmail}
            onStepClick={setCurrentStep}
            navigationDisabled={shouldHideStepNavigation}
          />

          <div className="flex flex-col px-4 py-5 pb-28 sm:px-6 sm:py-6 sm:pb-32 md:px-12 md:pb-6 lg:flex-1 lg:px-16 lg:py-8">
            <div className="flex flex-col lg:flex-1">
              {registrationCompleteEmail ? (
                <CompletionScreen
                  email={registrationCompleteEmail}
                  donationId={completedDonationId}
                  tenantSlug={tenantSlug}
                  receiptAvailable={!!receiptStatus?.taxReceiptAvailable}
                />
              ) : null}

              {!registrationCompleteEmail && (
                <>
                  {/* Step 1: Choose Ride */}
                  {currentStep === 1 && (
                    <div>
                      <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
                        {t('findYourRide')}
                      </h1>
                      <p className="text-stone-400 mb-8">{t('selectCity')}</p>

                      {isCampaignClosed && (
                        <div
                          role="alert"
                          className="mb-6 rounded-lg border border-amber-500/40 bg-amber-500/10 p-4"
                        >
                          <div className="flex items-start gap-3">
                            <AlertCircle className="mt-0.5 h-5 w-5 text-amber-400 flex-shrink-0" />
                            <div>
                              <p className="font-medium text-amber-100">
                                {t('campaignClosedTitle')}
                              </p>
                              <p className="mt-1 text-sm text-stone-300">
                                {customClosedMessage ?? t('campaignClosedBody')}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {init.isBlocked && (
                        <div className="mb-6 rounded-lg border border-emerald-700 bg-emerald-950/30 p-4">
                          <div className="flex items-start gap-3">
                            <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-400" />
                            <div>
                              <p className="font-medium text-emerald-100">
                                {t('alreadyRegisteredMessage')}
                              </p>
                              <p className="mt-1 text-sm text-stone-400">
                                {t('alreadyRegisteredChangeRide')}
                              </p>
                              <div className="mt-3 flex flex-wrap items-center gap-3">
                                <Button
                                  type="button"
                                  asChild
                                  className="bg-dgr-gold text-stone-950 hover:bg-dgr-gold-light"
                                >
                                  <a href="/my/dashboard">{t('goToDashboard')}</a>
                                </Button>
                                <Button
                                  type="button"
                                  variant="outline"
                                  className="border-stone-700 bg-stone-900 text-stone-100 hover:bg-stone-800 hover:text-white"
                                  onClick={() => {
                                    fetch('/api/auth/sign-out', {
                                      method: 'POST',
                                      credentials: 'include',
                                    }).then(() => {
                                      window.location.href = '/register';
                                    });
                                  }}
                                >
                                  {t('registerNewRider')}
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="space-y-4">
                        {/* Country */}
                        <div className="space-y-2">
                          <Label className="text-stone-300">{t('country')}</Label>
                          <CountrySelect
                            countries={countriesData.map((c: CountryData) => ({
                              code: c.code,
                              name: c.name,
                            }))}
                            value={rideCountryCode}
                            onValueChange={handleCountryChange}
                            placeholder={t('selectCountry')}
                            disabled={isLoadingCountries}
                            className="h-12 bg-stone-900 border-stone-700 text-white hover:text-white"
                            popoverClassName="bg-stone-900 border-stone-700 text-white [&_[cmdk-item]]:text-white [&_[cmdk-item][data-selected=true]]:bg-stone-800"
                          />
                        </div>

                        {/* State - only show if country has states/regions */}
                        {shouldShowStateSelection && (
                          <div ref={step1StateRef} className="space-y-2">
                            <Label className="text-stone-300">{t('stateRegion')}</Label>
                            <Combobox
                              options={statesData.map((s: StateData) => ({
                                value: s.code,
                                label: s.name,
                              }))}
                              value={rideStateCode}
                              onValueChange={handleStateChange}
                              placeholder={t('selectState')}
                              searchPlaceholder={t('searchStates')}
                              disabled={isLoadingStates}
                              className="h-12 bg-stone-900 border-stone-700 text-white hover:text-white"
                              popoverClassName="bg-stone-900 border-stone-700 text-white [&_[cmdk-item]]:text-white [&_[cmdk-item][data-selected=true]]:bg-stone-800"
                            />
                          </div>
                        )}

                        {/* Ride Selection - Show when state is selected OR when country has no states */}
                        {shouldShowRideSelection && (
                          <div ref={step1RideRef} className="space-y-3 pt-2">
                            <div className="flex items-center justify-between">
                              <Label className="text-stone-300">{t('selectYourRide')}</Label>
                              {!isLoadingRides && ridesData.length > 0 && (
                                <span className="text-stone-500 text-sm">
                                  {t('ridesAvailableCount', { count: ridesData.length })}
                                </span>
                              )}
                            </div>
                            <Combobox
                              options={ridesData.map((ride: RideData) => ({
                                value: ride.id,
                                label: ride.name,
                              }))}
                              value={rideId}
                              onValueChange={handleRideChange}
                              placeholder={isLoadingRides ? t('loadingRides') : t('chooseARide')}
                              searchPlaceholder={`${tCommon('search')}...`}
                              emptyMessage={t('noRidesAvailable')}
                              disabled={isLoadingRides}
                              className="h-12 bg-stone-900 border-stone-700 text-white hover:text-white"
                              popoverClassName="bg-stone-900 border-stone-700 text-white [&_[cmdk-item]]:text-white [&_[cmdk-item][data-selected=true]]:bg-stone-800"
                              renderValue={(option) => {
                                const ride =
                                  ridesData.find(
                                    (candidate: RideData) => candidate.id === option.value,
                                  ) ?? selectedRideData;

                                if (!ride) {
                                  return option.label;
                                }

                                return (
                                  <span className="flex min-w-0 items-center gap-3">
                                    <MapPin className="w-4 h-4 shrink-0 text-dgr-gold" />
                                    <span className="truncate font-medium">{ride.name}</span>
                                  </span>
                                );
                              }}
                              renderOption={(option) => {
                                const ride = ridesData.find(
                                  (candidate: RideData) => candidate.id === option.value,
                                );

                                if (!ride) {
                                  return option.label;
                                }

                                const capacityBadge = getCapacityBadgeValue(ride.capacity);
                                const minimumDonationBadge = parseRideMinimumDonation(
                                  ride.minimumDonation,
                                );

                                return (
                                  <div className="flex w-full items-center justify-between gap-3">
                                    <div className="flex min-w-0 items-center gap-3">
                                      <MapPin className="w-4 h-4 shrink-0 text-stone-500" />
                                      <span className="truncate font-medium">{ride.name}</span>
                                    </div>
                                    <div className="ml-auto flex shrink-0 items-center gap-2">
                                      {capacityBadge && (
                                        <Badge className="border-stone-600 bg-stone-800 text-stone-300">
                                          {t('capacityBadge', { capacity: capacityBadge })}
                                        </Badge>
                                      )}
                                      {minimumDonationBadge > 0 && (
                                        <Badge className="border-stone-600 bg-stone-800 text-stone-300">
                                          {t(
                                            'minimumDonationBadge',
                                            { amount: minimumDonationBadge },
                                            {
                                              number: {
                                                currency: {
                                                  style: 'currency',
                                                  currency: selectedCurrency,
                                                },
                                              },
                                            },
                                          )}
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                );
                              }}
                            />
                            {rideId && isSelectedRideCancelled && (
                              <div className="mt-3 p-4 rounded-lg border border-red-700 bg-red-950/40">
                                <div className="flex items-start gap-3">
                                  <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
                                  <div className="flex-1">
                                    <p className="text-red-200 font-medium">
                                      {selectedRideData?.name ?? 'This ride'} has been cancelled
                                    </p>
                                    <p className="text-red-100/90 text-sm mt-1">
                                      Registration is closed for this ride. Please choose another
                                      ride to continue.
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )}
                            {rideId && isSelectedRidePostponed && !isSelectedRideCancelled && (
                              <div className="mt-3 p-4 rounded-lg border border-amber-700 bg-amber-950/30">
                                <div className="flex items-start gap-3">
                                  <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
                                  <div className="flex-1">
                                    <p className="text-amber-200 font-medium">
                                      {selectedRideData?.name ?? 'This ride'} has been postponed
                                      {selectedRidePostponedDateLabel
                                        ? ` to ${selectedRidePostponedDateLabel}`
                                        : ''}
                                    </p>
                                    <p className="text-amber-100/90 text-sm mt-1">
                                      You can still register — your spot will be held for the new
                                      date.
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {shouldShowWaitlistToggle && (
                          <div
                            ref={step1WaitlistRef}
                            className="mt-4 p-4 rounded-lg border border-amber-700 bg-amber-950/30"
                          >
                            <div className="flex items-start gap-3">
                              <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5" />
                              <div className="flex-1">
                                <p className="text-amber-200 font-medium">
                                  {t('waitlistCapacityReachedTitle')}
                                </p>
                                <p className="text-amber-100/90 text-sm mt-1">
                                  {t('waitlistCapacityReachedDescription')}
                                </p>
                                <div className="flex items-center justify-between gap-3 mt-3">
                                  <Label htmlFor="join-waitlist" className="text-amber-100 text-sm">
                                    {t('waitlistJoinToggleLabel')}
                                  </Label>
                                  <Switch
                                    id="join-waitlist"
                                    checked={joinWaitlist}
                                    onCheckedChange={(checked) => setJoinWaitlist(checked)}
                                    className="data-[state=checked]:bg-dgr-gold"
                                  />
                                </div>
                                {hasMandatoryDonation && (
                                  <p className="text-amber-200/90 text-xs mt-2">
                                    {t('waitlistSkipsPaymentNotice')}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Waiver - shown when ride is selected */}
                        {shouldShowWaiverCard && (
                          <div
                            ref={step1WaiverRef}
                            className={`
                          p-4 rounded-lg border transition-all mt-4
                          ${waiverAccepted ? 'bg-yellow-950/30 border-yellow-800' : 'bg-stone-900 border-stone-800'}
                        `}
                          >
                            <div className="flex items-start gap-3">
                              <Checkbox
                                id="waiver-step1"
                                checked={waiverAccepted}
                                onCheckedChange={(checked) => setWaiverAccepted(checked === true)}
                                className="mt-0.5 border-stone-600 data-[state=checked]:bg-yellow-600 data-[state=checked]:border-yellow-600"
                              />
                              <div>
                                <Label
                                  htmlFor="waiver-step1"
                                  className="text-white font-medium cursor-pointer flex items-center gap-2"
                                >
                                  {tWaiver('step1.title')}
                                  <span className="text-red-400">*</span>
                                </Label>
                                <p className="text-stone-400 text-sm mt-1.5 leading-relaxed">
                                  {tWaiver('step1.agreement')}{' '}
                                  <button
                                    type="button"
                                    onClick={() => setWaiverDialogOpen(true)}
                                    className="text-dgr-gold hover:underline inline-flex items-center gap-1"
                                  >
                                    {tWaiver('step1.waiverLink')}
                                    <ExternalLink className="w-3 h-3" />
                                  </button>
                                  . {tWaiver('step1.understand')}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Step 2: Account & Details - Uses shared AuthStep component */}
                  {currentStep === 2 && (
                    <AuthStep
                      cityName={selectedRideData?.name}
                      onSuccess={handleAuthStepSuccess}
                      onBack={() => setCurrentStep(1)}
                      eventId={rideId}
                      joinWaitlist={joinWaitlist}
                      countryCode={rideCountryCode}
                      auth={registrationAuth}
                      authenticatedProfile={authenticatedProfile}
                    />
                  )}

                  {/* Step 3: Preferences */}
                  {currentStep === 3 && (
                    <div>
                      <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
                        {t('almostThere')}
                      </h1>
                      <p className="text-stone-400 mb-8">{t('reviewDetails')}</p>

                      {shouldSkipPaymentStep && (
                        <div className="mb-6 p-4 rounded-lg border border-amber-700 bg-amber-950/30">
                          <div className="flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5" />
                            <p className="text-amber-100 text-sm leading-relaxed">
                              {t('waitlistSkipsPaymentNotice')}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Summary Card */}
                      <div className="bg-stone-900 rounded-xl p-5 mb-8 border border-stone-800">
                        <h3 className="mb-4 text-stone-400 text-sm font-medium">
                          {t('registrationSummary')}
                        </h3>
                        <div className="space-y-3">
                          {/* Ride — tappable to change */}
                          <button
                            type="button"
                            onClick={handleChangeRideShortcut}
                            className="group -mx-2 flex w-[calc(100%+1rem)] items-center gap-3 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-stone-800/60"
                          >
                            <MapPin className="h-5 w-5 shrink-0 text-dgr-gold" />
                            <span className="flex-1 text-white">
                              {selectedRideData?.name}
                              {(rideCountryCode || rideStateCode) && (
                                <span className="ml-1 text-stone-400">
                                  ({rideStateCode ? `${rideStateCode}, ` : ''}
                                  {rideCountryCode})
                                </span>
                              )}
                            </span>
                            <span className="shrink-0 text-xs font-medium text-dgr-gold opacity-0 transition-opacity group-hover:opacity-100 sm:opacity-100">
                              {t('changeYourRide')}
                            </span>
                          </button>
                          <div className="flex items-center gap-3 text-white">
                            <Calendar className="h-5 w-5 text-dgr-gold" />
                            <span>Sunday, May 17th 2026</span>
                          </div>
                          {/* Show entrant data from Zustand store after successful auth */}
                          {entrant && (
                            <div className="flex items-center gap-3 text-white">
                              <Users className="h-5 w-5 text-dgr-gold" />
                              <span>
                                {entrant.firstName} {entrant.lastName}
                              </span>
                            </div>
                          )}
                          {/* Vehicle display */}
                          {vehicle && (
                            <div className="flex items-center gap-3 text-white">
                              <Bike className="h-5 w-5 text-dgr-gold" />
                              <span>
                                {vehicle.vehicleYear} {vehicle.vehicleMake} {vehicle.vehicleModel}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Stay Updated - Consolidated */}
                      <div className="p-5 rounded-xl bg-stone-900 border border-stone-800">
                        <h3 className="text-white font-semibold mb-4">{t('stayUpdated')}</h3>
                        <div className="space-y-4">
                          <div className="flex items-start gap-3">
                            <Checkbox
                              id="newsletter"
                              checked={newsletterOptIn}
                              onCheckedChange={(checked) => setNewsletterOptIn(checked === true)}
                              className="mt-0.5 border-stone-600 data-[state=checked]:bg-dgr-gold data-[state=checked]:border-dgr-gold"
                            />
                            <Label
                              htmlFor="newsletter"
                              className="text-stone-300 text-sm cursor-pointer leading-relaxed"
                            >
                              {t('newsletterOptIn')}
                            </Label>
                          </div>
                          <div className="flex items-start gap-3">
                            <Checkbox
                              id="triumphMarketing"
                              checked={triumphMarketingOptIn}
                              onCheckedChange={(checked) =>
                                setTriumphMarketingOptIn(checked === true)
                              }
                              className="mt-0.5 border-stone-600 data-[state=checked]:bg-dgr-gold data-[state=checked]:border-dgr-gold"
                            />
                            <Label
                              htmlFor="triumphMarketing"
                              className="text-stone-300 text-sm cursor-pointer leading-relaxed"
                            >
                              {t('triumphMarketingOptIn')}
                            </Label>
                          </div>
                          <div className="flex items-start gap-3">
                            <Checkbox
                              id="movemberMarketing"
                              checked={movemberMarketingOptIn}
                              onCheckedChange={(checked) =>
                                setMovemberMarketingOptIn(checked === true)
                              }
                              className="mt-0.5 border-stone-600 data-[state=checked]:bg-dgr-gold data-[state=checked]:border-dgr-gold"
                            />
                            <Label
                              htmlFor="movemberMarketing"
                              className="text-stone-300 text-sm cursor-pointer leading-relaxed"
                            >
                              {t('movemberMarketingOptIn')}
                            </Label>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Step 4: Payment */}
                  {currentStep === 4 && (
                    <div>
                      {isCheckoutFinalizing ? (
                        <div className="rounded-xl border border-stone-800 bg-stone-900 p-6">
                          <div className="flex flex-col items-center gap-4 py-8 text-center">
                            {finalizingTimedOut ? (
                              <>
                                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-amber-500/20">
                                  <AlertCircle className="h-7 w-7 text-amber-400" />
                                </div>
                                <div className="space-y-2">
                                  <h1 className="text-2xl font-bold text-white">
                                    {tDonate('form.finalizingTimedOutTitle')}
                                  </h1>
                                  <p className="text-stone-400">
                                    {tDonate('form.finalizingTimedOutMessage')}
                                  </p>
                                </div>
                                <div className="flex flex-wrap items-center justify-center gap-3">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setFinalizingTimedOut(false)}
                                    className="border-stone-700 bg-stone-800 text-stone-300 hover:bg-stone-700"
                                  >
                                    {tDonate('form.keepWaiting')}
                                  </Button>
                                </div>
                              </>
                            ) : (
                              <>
                                <Loader2 className="h-10 w-10 animate-spin text-dgr-gold" />
                                <div className="space-y-2">
                                  <h1 className="text-2xl font-bold text-white">
                                    {tDonate('form.finalizingPayment')}
                                  </h1>
                                  <p className="text-stone-400">
                                    {tDonate('form.finalizingMessage')}
                                  </p>
                                  {entrant?.email ? (
                                    <p className="text-sm text-stone-500">
                                      {tDonate('form.finalizingEmail', { email: entrant.email })}
                                    </p>
                                  ) : null}
                                </div>
                              </>
                            )}
                          </div>

                          {redirectSession && checkoutConfig ? (
                            <div className="hidden">
                              <AdyenCheckout
                                session={{
                                  id: redirectSession.sessionId,
                                  sessionData: redirectSession.sessionData,
                                }}
                                config={checkoutConfig}
                                redirectResult={redirectSession.redirectResult}
                                onPaymentCompleted={handleRedirectPaymentCompleted}
                                onPaymentFailed={handleRedirectPaymentFailed}
                                onReady={handleCheckoutReady}
                                onError={handleCheckoutError}
                              />
                            </div>
                          ) : null}
                        </div>
                      ) : isAwaitingSettlement ? (
                        <div className="rounded-xl border border-stone-800 bg-stone-900 p-6">
                          <div className="flex flex-col items-center gap-4 py-8 text-center">
                            {settlementTimedOut ? (
                              <>
                                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-amber-500/20">
                                  <AlertCircle className="h-7 w-7 text-amber-400" />
                                </div>
                                <div className="space-y-2">
                                  <h1 className="text-2xl font-bold text-white">
                                    {tDonate('form.finalizingTimedOutTitle')}
                                  </h1>
                                  <p className="text-stone-400">
                                    {tDonate('form.awaitingSettlementTimeout')}
                                  </p>
                                </div>
                                <div className="flex flex-wrap items-center justify-center gap-3">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => {
                                      track('rego_settlement_keep_waiting', {
                                        donation_id: returnDonationId ?? undefined,
                                        poll_duration_ms: Date.now() - settlementStartRef.current,
                                      });
                                      setSettlementTimedOut(false);
                                    }}
                                    className="border-stone-700 bg-stone-800 text-stone-300 hover:bg-stone-700"
                                  >
                                    {tDonate('form.keepWaiting')}
                                  </Button>
                                </div>
                              </>
                            ) : (
                              <>
                                <Loader2 className="h-10 w-10 animate-spin text-dgr-gold" />
                                <div className="space-y-2">
                                  <h1 className="text-2xl font-bold text-white">
                                    {tDonate('form.awaitingSettlement')}
                                  </h1>
                                  <p className="text-stone-400">
                                    {tDonate('form.awaitingSettlementMessage')}
                                  </p>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      ) : isCheckoutFailed ? (
                        <div
                          className={`rounded-xl border p-6 ${
                            paymentReceivedButIncomplete
                              ? 'border-emerald-500/30 bg-emerald-500/10'
                              : 'border-red-500/30 bg-red-500/10'
                          }`}
                        >
                          <div className="flex flex-col items-center gap-4 py-6 text-center">
                            <div
                              className={`flex h-14 w-14 items-center justify-center rounded-full ${
                                paymentReceivedButIncomplete ? 'bg-emerald-500/20' : 'bg-red-500/20'
                              }`}
                            >
                              {paymentReceivedButIncomplete ? (
                                <CheckCircle2 className="h-7 w-7 text-emerald-400" />
                              ) : (
                                <AlertCircle className="h-7 w-7 text-red-400" />
                              )}
                            </div>
                            <div className="space-y-2">
                              <h1 className="text-2xl font-bold text-white">
                                {paymentReceivedButIncomplete
                                  ? tDonate('form.paymentReceivedTitle')
                                  : tDonate('form.paymentFailed')}
                              </h1>
                              <p className="text-stone-300">
                                {paymentReceivedButIncomplete
                                  ? tDonate('form.paymentReceivedMessage')
                                  : (submitError ?? tDonate('form.paymentFailedMessage'))}
                              </p>
                            </div>
                            <div className="flex flex-wrap items-center justify-center gap-3">
                              {paymentReceivedButIncomplete ? (
                                <Button
                                  type="button"
                                  onClick={() => (window.location.href = '/login')}
                                  className="bg-dgr-gold text-stone-950 hover:bg-dgr-gold-light"
                                >
                                  {tDonate('form.logIn')}
                                </Button>
                              ) : (
                                <>
                                  <Button
                                    type="button"
                                    onClick={() => {
                                      if (redirectSession || checkoutSession) {
                                        void retryRegistrationCompletion();
                                        return;
                                      }
                                      resetRegistrationCheckout();
                                    }}
                                    className="bg-dgr-gold text-stone-950 hover:bg-dgr-gold-light"
                                  >
                                    {tDonate('form.tryAgain')}
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => {
                                      resetRegistrationCheckout({ clearError: false });
                                      setCurrentStep(3);
                                    }}
                                    className="border-stone-700 bg-stone-800 text-stone-300 hover:bg-stone-700"
                                  >
                                    {tCommon('back')}
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <>
                          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
                            {tDonate('supportTheCause')}
                          </h1>
                          <p className="text-stone-400 mb-6">
                            {hasMandatoryDonation
                              ? tDonate(
                                  'minimumRequired',
                                  {
                                    amount: mandatoryDonation,
                                    location: selectedRideData?.name ?? '',
                                  },
                                  {
                                    number: {
                                      currency: {
                                        style: 'currency',
                                        currency: checkoutCurrencyCode,
                                      },
                                    },
                                  },
                                )
                              : tDonate('subtitleNoMinimum')}
                          </p>

                          {/* Donation Section */}
                          <div className="space-y-6">
                            <div className="bg-stone-900 rounded-xl p-5 border border-stone-800">
                              <div className="flex items-center gap-2 mb-4">
                                <Heart className="w-5 h-5 text-dgr-gold" />
                                <h3 className="text-white font-semibold">
                                  {tDonate('donationLabel')}{' '}
                                  {hasMandatoryDonation && <span className="text-red-400">*</span>}
                                </h3>
                              </div>

                              {/* Preset Amounts - filtered to only show amounts >= minimum, stretch to fit */}
                              {(() => {
                                const gridCols =
                                  donationPresetAmounts.length <= 2
                                    ? 'grid-cols-2'
                                    : donationPresetAmounts.length === 3
                                      ? 'grid-cols-2 sm:grid-cols-3'
                                      : 'grid-cols-2 sm:grid-cols-4';
                                return (
                                  <div className={`grid ${gridCols} gap-2 mb-4`}>
                                    {donationPresetAmounts.map((amount) => {
                                      const isSelected =
                                        donationAmount === amount && !customDonation;
                                      return (
                                        <button
                                          key={amount}
                                          type="button"
                                          disabled={isCheckoutLocked}
                                          onClick={() => {
                                            // Toggle selection - click again to unselect
                                            // Store's setDonationAmount automatically clears customDonation
                                            setDonationAmount(isSelected ? null : amount);
                                          }}
                                          className={`
                              h-12 rounded-lg border text-sm font-semibold transition-all
                              ${
                                isSelected
                                  ? 'bg-dgr-gold border-dgr-gold text-stone-950'
                                  : 'bg-stone-800 border-stone-700 text-white hover:border-stone-600'
                              }
                              ${isCheckoutLocked ? 'opacity-60 cursor-not-allowed' : ''}
                            `}
                                        >
                                          {format.number(amount, {
                                            style: 'currency',
                                            currency: checkoutCurrencyCode,
                                          })}
                                        </button>
                                      );
                                    })}
                                  </div>
                                );
                              })()}

                              {/* Custom Amount - Input group with currency prefix */}
                              <div className="flex">
                                <div className="flex items-center justify-center px-4 h-12 bg-stone-700 border border-r-0 border-stone-600 rounded-l-lg">
                                  <span className="text-stone-300 font-medium">
                                    {currencySymbol}
                                  </span>
                                </div>
                                <Input
                                  type="number"
                                  placeholder={tDonate('otherAmount')}
                                  value={customDonation}
                                  onChange={(e) => setCustomDonation(e.target.value)}
                                  onFocus={() => setDonationAmount(null)}
                                  disabled={isCheckoutLocked}
                                  className="h-12 rounded-l-none bg-stone-800 border-stone-700 text-white placeholder:text-stone-500 flex-1"
                                  min={hasMandatoryDonation ? registrationDonationFloor : 0}
                                />
                              </div>

                              {hasMandatoryDonation &&
                                orderTotal > 0 &&
                                registrationOrderShortfall > 0 && (
                                  <p className="text-red-400 text-sm mt-2">
                                    {tDonate(
                                      'minimumDonation',
                                      {
                                        amount: mandatoryDonation,
                                      },
                                      {
                                        number: {
                                          currency: {
                                            style: 'currency',
                                            currency: checkoutCurrencyCode,
                                          },
                                        },
                                      },
                                    )}
                                  </p>
                                )}
                            </div>

                            {/* Merchandise Section */}
                            <div className="bg-stone-900 rounded-xl p-5 border border-stone-800">
                              <div className="flex items-center gap-2 mb-4">
                                <Package className="w-5 h-5 text-dgr-gold" />
                                <h3 className="text-white font-semibold">
                                  {tDonate('addMerchandise')}
                                </h3>
                                <span className="text-stone-500 text-sm">
                                  ({tDonate('optional')})
                                </span>
                              </div>

                              <div className="space-y-3">
                                {catalogItems.map((item) => {
                                  const isAdded = (selectedMerch[item.id] || 0) > 0;
                                  return (
                                    <div
                                      key={item.id}
                                      className={`
                                p-4 rounded-lg border transition-all
                                ${isAdded ? 'bg-stone-800/50 border-dgr-gold/50' : 'bg-stone-800/30 border-stone-700'}
                              `}
                                    >
                                      <div className="flex items-start gap-4">
                                        {/* Product Image - Clickable for preview */}
                                        {item.imageUrl && (
                                          <button
                                            type="button"
                                            onClick={() => {
                                              setMerchPreviewItem(item);
                                              setMerchPreviewOpen(true);
                                            }}
                                            className="relative w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-stone-700 cursor-zoom-in group"
                                          >
                                            {/* eslint-disable-next-line @next/next/no-img-element */}
                                            <img
                                              src={item.imageUrl}
                                              alt={item.name}
                                              className="absolute inset-0 h-full w-full object-cover transition-transform group-hover:scale-110"
                                            />
                                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                                              <Eye className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                            </div>
                                          </button>
                                        )}
                                        <div className="flex-1 min-w-0">
                                          <h4 className="text-white font-medium">{item.name}</h4>
                                          <p className="text-stone-400 text-sm mt-0.5">
                                            {item.description}
                                          </p>
                                          <div className="flex items-center justify-between gap-3 mt-2">
                                            <p className="text-dgr-gold font-semibold">
                                              {format.number(item.price, {
                                                style: 'currency',
                                                currency: item.currencyCode,
                                              })}
                                            </p>
                                            {isAdded ? (
                                              <button
                                                type="button"
                                                disabled={isCheckoutLocked}
                                                onClick={() => removeMerch(item.id)}
                                                className="px-3 py-1.5 rounded-lg bg-stone-700 text-white text-sm font-medium flex items-center gap-2 hover:bg-stone-600 disabled:opacity-60 disabled:cursor-not-allowed"
                                              >
                                                <Check className="w-3.5 h-3.5 text-dgr-gold" />
                                                {item.allowMultiple
                                                  ? `${selectedMerch[item.id]}`
                                                  : tDonate('added')}
                                              </button>
                                            ) : (
                                              <button
                                                type="button"
                                                disabled={isCheckoutLocked}
                                                onClick={() => updateMerchQuantity(item.id, 1)}
                                                className="px-3 py-1.5 rounded-lg bg-dgr-gold text-stone-950 text-sm font-medium hover:bg-dgr-gold-light disabled:opacity-60 disabled:cursor-not-allowed"
                                              >
                                                {tDonate('add')}
                                              </button>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>

                            {/* Shipping Address - Only if selected items require shipping */}
                            {requiresShipping && (
                              <div
                                ref={step4ShippingRef}
                                className={`scroll-mt-24 rounded-xl border bg-stone-900 p-4 sm:scroll-mt-28 sm:p-5 ${shouldShowShippingValidation ? 'border-amber-500/40' : 'border-stone-800'}`}
                              >
                                <div className="flex items-center gap-2 mb-4">
                                  <Truck className="w-5 h-5 text-dgr-gold" />
                                  <h3 className="text-white font-semibold">
                                    {tDonate('shippingAddress')}
                                  </h3>
                                </div>

                                {shouldShowShippingValidation ? (
                                  <div className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3.5 sm:p-4">
                                    <div className="flex items-start gap-3">
                                      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" />
                                      <div className="min-w-0">
                                        <p className="text-sm font-medium text-amber-100">
                                          {t('shippingAddressRequiredTitle')}
                                        </p>
                                        <p className="mt-1 text-sm text-amber-200/80">
                                          {t('shippingAddressRequiredMessage')}
                                        </p>
                                      </div>
                                    </div>
                                    <div className="mt-3 flex flex-wrap gap-2">
                                      {missingShippingFields.map((field) => (
                                        <span
                                          key={field}
                                          className="rounded-full border border-amber-400/30 bg-stone-950/40 px-2.5 py-1 text-xs text-amber-100"
                                        >
                                          {shippingFieldLabels[field]}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                ) : null}

                                <DonorAddressFields
                                  address={{
                                    streetAddress: shippingAddress,
                                    addressLine2: shippingAddressLine2,
                                    city: shippingCity,
                                    state: shippingState,
                                    postalCode: shippingPostcode,
                                    country: shippingCountry,
                                  }}
                                  taxNumber=""
                                  showTaxNumber={false}
                                  requiredFields={
                                    shouldShowShippingValidation ? missingShippingFields : []
                                  }
                                  countries={allShippingCountries}
                                  googlePlacesApiKey={googlePlacesApiKey}
                                  onAddressChange={(field, value) => {
                                    const fieldMap: Record<keyof DonorAddress, string> = {
                                      streetAddress: 'address',
                                      addressLine2: 'addressLine2',
                                      city: 'city',
                                      state: 'state',
                                      postalCode: 'postcode',
                                      country: 'country',
                                    };
                                    setShipping({ [fieldMap[field]]: value });
                                  }}
                                  onTaxNumberChange={() => {}}
                                  onCountryChange={(code) => {
                                    setShipping({ country: code, state: '' });
                                  }}
                                />
                              </div>
                            )}

                            {/* Order Summary */}
                            {(actualDonation > 0 || merchTotal > 0) && (
                              <div className="rounded-xl border border-stone-700 bg-stone-800/50 p-4 sm:p-5">
                                <h3 className="text-stone-400 text-sm font-medium  mb-4">
                                  {tDonate('orderSummary')}
                                </h3>
                                <div className="space-y-2 text-sm">
                                  {actualDonation > 0 && (
                                    <div className="flex items-start justify-between gap-3 text-white">
                                      <span className="min-w-0 flex-1">
                                        {tDonate('donationLabel')}
                                      </span>
                                      <span className="shrink-0 text-right">
                                        {format.number(actualDonation, {
                                          style: 'currency',
                                          currency: checkoutCurrencyCode,
                                        })}
                                      </span>
                                    </div>
                                  )}
                                  {selectedCatalogItems.map(({ item, quantity }) => {
                                    return (
                                      <div
                                        key={item.id}
                                        className="flex items-start justify-between gap-3 text-white"
                                      >
                                        <span className="min-w-0 flex-1 break-words">
                                          {tDonate('merchLineItem', { name: item.name, quantity })}
                                        </span>
                                        <span className="shrink-0 text-right">
                                          {format.number(item.price * quantity, {
                                            style: 'currency',
                                            currency: item.currencyCode,
                                          })}
                                        </span>
                                      </div>
                                    );
                                  })}
                                  {includePlatformFee && platformFeeAmount > 0 && (
                                    <div className="flex items-start justify-between gap-3 text-white">
                                      <span className="min-w-0 flex-1">
                                        {tForm('platformFeeLabel')}
                                      </span>
                                      <span className="shrink-0 text-right">
                                        {format.number(platformFeeAmount, {
                                          style: 'currency',
                                          currency: checkoutCurrencyCode,
                                        })}
                                      </span>
                                    </div>
                                  )}
                                  <div className="h-px bg-stone-700 my-3" />
                                  <div className="flex items-start justify-between gap-3 text-base font-semibold text-white">
                                    <span className="min-w-0 flex-1">{tDonate('total')}</span>
                                    <span className="shrink-0 text-right text-dgr-gold">
                                      {format.number(payableTotal, {
                                        style: 'currency',
                                        currency: checkoutCurrencyCode,
                                      })}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Platform fee opt-in */}
                            {feeConfig && platformFeeAmount > 0 && (
                              <div className="bg-stone-800/50 rounded-lg p-4 border border-stone-700">
                                <div className="flex items-start gap-3">
                                  <input
                                    type="checkbox"
                                    id="registration-platform-fee"
                                    checked={includePlatformFee}
                                    onChange={(e) => setIncludePlatformFee(e.target.checked)}
                                    disabled={isCheckoutLocked}
                                    className="rounded border-stone-600 bg-stone-800 text-dgr-gold focus:ring-dgr-gold w-4 h-4 mt-0.5"
                                  />
                                  <label
                                    htmlFor="registration-platform-fee"
                                    className="cursor-pointer"
                                  >
                                    <span className="text-white text-sm font-medium block">
                                      {tForm('platformFeeLabel')}
                                    </span>
                                    <span className="text-stone-400 text-xs">
                                      {tForm('platformFeeDescription', {
                                        amount: format.number(platformFeeAmount, {
                                          style: 'currency',
                                          currency: checkoutCurrencyCode,
                                        }),
                                      })}
                                    </span>
                                  </label>
                                </div>
                              </div>
                            )}

                            {/* Secure checkout */}
                            {payableTotal > 0 && (
                              <div
                                ref={step4PaymentRef}
                                className="scroll-mt-24 rounded-xl border border-stone-800 bg-stone-900 p-4 sm:scroll-mt-28 sm:p-5"
                              >
                                <div className="flex items-center gap-2 mb-4">
                                  <CreditCard className="w-5 h-5 text-dgr-gold" />
                                  <h3 className="text-white font-semibold">
                                    {tDonate('paymentMethod')}{' '}
                                    <span className="text-red-400">*</span>
                                  </h3>
                                </div>
                                <div className="space-y-4">
                                  {shouldShowShippingValidation ? (
                                    <div className="flex items-start gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-3 text-sm">
                                      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" />
                                      <p className="text-amber-100">
                                        {t('shippingAddressPaymentHint')}
                                      </p>
                                    </div>
                                  ) : null}

                                  <p className="text-sm text-stone-400">
                                    {isCheckoutPaymentActive || isCheckoutCreatingSession
                                      ? tDonate('creditDebitCard')
                                      : tDonate(
                                          'pay',
                                          { amount: payableTotal },
                                          {
                                            number: {
                                              currency: {
                                                style: 'currency',
                                                currency: checkoutCurrencyCode,
                                              },
                                            },
                                          },
                                        )}
                                  </p>

                                  {isPaymentConfigLoading || isPaymentConfigFetching ? (
                                    <div className="rounded-lg border border-stone-700 bg-stone-800/50 p-4 text-sm text-stone-400">
                                      {tCommon('loading')}
                                    </div>
                                  ) : null}

                                  {isPaymentConfigError ? (
                                    <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300">
                                      {tCommon('error')}
                                    </div>
                                  ) : null}

                                  {/* Single loading state — covers session creation + Adyen SDK init */}
                                  {isCheckoutCreatingSession ||
                                  (isCheckoutPaymentActive && !checkoutReady) ? (
                                    <div className="rounded-lg border border-stone-700 bg-stone-800/50 p-6 text-center">
                                      <Loader2 className="mx-auto h-8 w-8 animate-spin text-dgr-gold" />
                                      <p className="mt-3 font-medium text-white">
                                        {tDonate('form.preparingCheckout')}
                                      </p>
                                      <p className="mt-1 text-sm text-stone-400">
                                        {tDonate('form.preparingCheckoutMessage')}
                                      </p>
                                    </div>
                                  ) : null}

                                  {/* Adyen drop-in — mounts when session exists so SDK loads behind the spinner */}
                                  {checkoutSession &&
                                  checkoutConfig &&
                                  (isCheckoutPaymentActive || isVoucherPending) ? (
                                    <div className="space-y-4">
                                      <div className={checkoutReady ? undefined : 'sr-only'}>
                                        <AdyenCheckout
                                          session={{
                                            id: checkoutSession.sessionId,
                                            sessionData: checkoutSession.sessionData,
                                          }}
                                          config={checkoutConfig}
                                          onPaymentCompleted={handleCheckoutPaymentCompleted}
                                          onPaymentFailed={handleCheckoutPaymentFailed}
                                          onBeforeSubmit={handleBeforeSubmit}
                                          onReady={handleCheckoutReady}
                                          onError={handleCheckoutError}
                                        />
                                      </div>
                                      {isVoucherPending ? (
                                        <div className="mt-4 rounded-xl border border-stone-800 bg-stone-900 p-5">
                                          <div className="flex items-start gap-3">
                                            <CheckCircle2 className="h-5 w-5 shrink-0 text-dgr-gold mt-0.5" />
                                            <div className="space-y-1">
                                              <p className="font-medium text-white">
                                                {tDonate('form.voucherPendingTitle')}
                                              </p>
                                              <p className="text-sm text-stone-400">
                                                {tDonate('form.voucherPendingMessage')}
                                              </p>
                                            </div>
                                          </div>
                                        </div>
                                      ) : checkoutReady ? (
                                        <div className="flex items-center justify-between gap-3">
                                          <Button
                                            type="button"
                                            variant="ghost"
                                            onClick={() =>
                                              resetRegistrationCheckout({ clearError: false })
                                            }
                                            className="text-stone-400 hover:bg-stone-800 hover:text-white"
                                          >
                                            {tCommon('back')}
                                          </Button>
                                          <div className="flex items-center justify-center gap-2 text-stone-600 text-xs">
                                            <Lock className="w-3 h-3" />
                                            <span>{tDonate('creditDebitCard')}</span>
                                          </div>
                                        </div>
                                      ) : null}
                                    </div>
                                  ) : null}

                                  {/* Native PayPal — shown as another option below Adyen */}
                                  {paypalAvailable &&
                                  checkoutSession &&
                                  isCheckoutPaymentActive &&
                                  checkoutReady ? (
                                    <div className="space-y-3 pt-2">
                                      <div className="flex items-center gap-3">
                                        <div className="h-px flex-1 bg-stone-700" />
                                        <span className="text-xs text-stone-500">or</span>
                                        <div className="h-px flex-1 bg-stone-700" />
                                      </div>
                                      <PayPalCheckout
                                        clientId={checkoutSession.paypalClientId!}
                                        currencyCode={checkoutCurrencyCode}
                                        createOrder={async () => {
                                          const donorEmail =
                                            entrant?.email ?? authUser?.email ?? '';
                                          const donorFirstName =
                                            entrant?.firstName ??
                                            authUser?.name?.split(' ')[0] ??
                                            '';
                                          const donorLastName =
                                            entrant?.lastName ??
                                            authUser?.name?.split(' ').slice(1).join(' ') ??
                                            '';
                                          const registrationCompletion =
                                            buildRegistrationCompletionState({
                                              eventId: rideId,
                                              termsAccepted: termsAcceptedForRegistration,
                                              newsletterOptIn,
                                              triumphMarketingOptIn,
                                              movemberMarketingOptIn,
                                            });
                                          checkoutCompletionRef.current = registrationCompletion;
                                          const session =
                                            await createRegistrationSession.mutateAsync({
                                              amount: payableTotal,
                                              donorEmail,
                                              donorFirstName,
                                              donorLastName,
                                              isAnonymous: false,
                                              paymentMethod: 'paypal',
                                              returnUrl:
                                                window.location.origin + window.location.pathname,
                                              donorCountryCode:
                                                toIsoCountryCode(shippingCountry) ||
                                                toIsoCountryCode(paymentConfigData?.countryCode) ||
                                                undefined,
                                              donorAddress: requiresShipping
                                                ? {
                                                    streetAddress: shippingAddress,
                                                    ...(shippingAddressLine2
                                                      ? { addressLine2: shippingAddressLine2 }
                                                      : {}),
                                                    city: shippingCity,
                                                    ...(shippingState
                                                      ? { state: shippingState }
                                                      : {}),
                                                    postalCode: shippingPostcode,
                                                    country: shippingCountry,
                                                  }
                                                : undefined,
                                              items: checkoutItemsInput,
                                              eventId: rideId,
                                              platformFee: includePlatformFee
                                                ? platformFeeAmount
                                                : 0,
                                              registrationCompletion,
                                            });
                                          if (session.paymentMethod !== 'paypal') {
                                            throw new Error('Expected PayPal session');
                                          }
                                          paypalDonationIdRef.current = session.donationId;
                                          return {
                                            donationId: session.donationId,
                                            orderId: session.orderId,
                                          };
                                        }}
                                        onCaptureOrder={async (params) =>
                                          capturePayPalOrder.mutateAsync(params)
                                        }
                                        onPaymentCompleted={handlePayPalPaymentCompleted}
                                        onPaymentFailed={handlePayPalPaymentFailed}
                                      />
                                    </div>
                                  ) : null}
                                </div>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* Navigation - Hidden on Step 2 (AuthStep handles own submission) and during active checkout */}
                  <NavigationBar
                    currentStep={currentStep}
                    canProceed={currentStep === 4 ? !!canSubmitStep4 : !!canProceed}
                    isLoading={isSubmittingRegistration}
                    onNext={() => {
                      if (currentStep < 4) {
                        if (currentStep === 1 && shouldSkipAuthStep) {
                          void handleSkipAuthContinue();
                          return;
                        }
                        if (currentStep === 3 && shouldSkipPaymentStep) {
                          void handleSubmit();
                          return;
                        }
                        setCurrentStep(currentStep + 1);
                      } else {
                        void handleSubmit();
                      }
                    }}
                    onBack={() => {
                      if (currentStep === 3 && shouldSkipAuthStep) {
                        setCurrentStep(1);
                        return;
                      }
                      setCurrentStep(currentStep - 1);
                    }}
                    nextLabel={
                      currentStep < 4
                        ? currentStep === 3 && shouldSkipPaymentStep
                          ? t('joinWaitlistButton')
                          : tCommon('continue')
                        : orderTotal > 0
                          ? tDonate(
                              'pay',
                              { amount: orderTotal },
                              {
                                number: {
                                  currency: {
                                    style: 'currency',
                                    currency: checkoutCurrencyCode,
                                  },
                                },
                              },
                            )
                          : tDonate('completeRegistration')
                    }
                    backLabel={tCommon('back')}
                    loadingLabel={tCommon('loading')}
                    showBack={currentStep > 1 && !shouldHideStepNavigation}
                    hidden={currentStep === 2 || (shouldHideStepNavigation && currentStep === 4)}
                    isKeyboardOpen={isKeyboardOpen}
                    stepLabel={currentStepLabel}
                    onSkip={
                      currentStep === 4 && !hasMandatoryDonation && orderTotal > 0
                        ? handleSkipAndComplete
                        : undefined
                    }
                    skipLabel={tDonate('skipAndComplete')}
                    skipLabelMobile={tDonate('skip')}
                  />

                  {submitError && !isCheckoutFailed && (
                    <p className="mt-4 text-sm text-red-400" role="alert">
                      {submitError}
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Waiver Dialog */}
      <Dialog open={waiverDialogOpen} onOpenChange={setWaiverDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] bg-stone-900 border-stone-700 text-white overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <div className="flex items-center justify-between pr-8">
              <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
                <Shield className="w-5 h-5 text-dgr-gold" />
                {tWaiver('title')}
              </DialogTitle>
            </div>
            <DialogDescription className="text-stone-400">
              {tWaiver('readCarefully')}
            </DialogDescription>
          </DialogHeader>

          {/* Toggle for Simplified/Full Waiver */}
          <div className="flex items-center justify-between py-3 px-4 bg-stone-800/50 rounded-lg border border-stone-700 flex-shrink-0">
            <div className="flex items-center gap-3">
              {simplifiedWaiver ? (
                <List className="w-5 h-5 text-dgr-gold" />
              ) : (
                <FileText className="w-5 h-5 text-dgr-gold" />
              )}
              <div>
                <p className="text-white font-medium text-sm">
                  {simplifiedWaiver ? tWaiver('simplifiedSummary') : tWaiver('fullDocument')}
                </p>
                <p className="text-stone-500 text-xs">
                  {simplifiedWaiver ? tWaiver('keyPoints') : tWaiver('completeText')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-stone-400 text-xs">{tWaiver('fullLabel')}</span>
              <Switch
                checked={!simplifiedWaiver}
                onCheckedChange={(checked) => setSimplifiedWaiver(!checked)}
                className="data-[state=checked]:bg-dgr-gold"
              />
              <span className="text-stone-400 text-xs">{tWaiver('simplified')}</span>
            </div>
          </div>

          {/* Waiver Content */}
          <div className="flex-1 overflow-y-auto pr-2 min-h-0">
            {simplifiedWaiver ? (
              /* Simplified Waiver - Dot Points */
              <div className="space-y-4 py-4">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                  <div key={num} className="flex items-start gap-3 p-3 rounded-lg bg-stone-800/30">
                    <div className="w-6 h-6 rounded-full bg-dgr-gold/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-dgr-gold text-sm font-bold">{num}</span>
                    </div>
                    <div>
                      <p className="text-white font-medium">
                        {tWaiver(`point${num}.title` as 'point1.title')}
                      </p>
                      <p className="text-stone-400 text-sm mt-1">
                        {tWaiver(`point${num}.desc` as 'point1.desc')}
                      </p>
                    </div>
                  </div>
                ))}

                <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                  <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <p className="text-amber-200/90 text-sm">{tWaiver('simplifiedNote')}</p>
                </div>
              </div>
            ) : (
              /* Full Waiver Text */
              <div className="py-4 space-y-6 text-stone-300 text-sm leading-relaxed">
                <section>
                  <h3 className="text-white font-semibold text-base mb-3">
                    {tWaiver('full.title')}
                  </h3>
                  <p>{tWaiver('full.intro')}</p>
                </section>

                <section>
                  <h3 className="text-white font-semibold text-base mb-3">
                    {tWaiver('full.section1.title')}
                  </h3>
                  <p className="mb-3">{tWaiver('full.section1.intro')}</p>
                  <ul className="list-disc list-inside space-y-1 text-stone-400 ml-4">
                    <li>{tWaiver('full.section1.risk1')}</li>
                    <li>{tWaiver('full.section1.risk2')}</li>
                    <li>{tWaiver('full.section1.risk3')}</li>
                    <li>{tWaiver('full.section1.risk4')}</li>
                    <li>{tWaiver('full.section1.risk5')}</li>
                  </ul>
                  <p className="mt-3">{tWaiver('full.section1.outro')}</p>
                </section>

                <section>
                  <h3 className="text-white font-semibold text-base mb-3">
                    {tWaiver('full.section2.title')}
                  </h3>
                  <p className="mb-3">{tWaiver('full.section2.intro')}</p>
                  <ul className="list-disc list-inside space-y-1 text-stone-400 ml-4">
                    <li>{tWaiver('full.section2.rep1')}</li>
                    <li>{tWaiver('full.section2.rep2')}</li>
                    <li>{tWaiver('full.section2.rep3')}</li>
                    <li>{tWaiver('full.section2.rep4')}</li>
                    <li>{tWaiver('full.section2.rep5')}</li>
                    <li>{tWaiver('full.section2.rep6')}</li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-white font-semibold text-base mb-3">
                    {tWaiver('full.section3.title')}
                  </h3>
                  <p>{tWaiver('full.section3.content')}</p>
                </section>

                <section>
                  <h3 className="text-white font-semibold text-base mb-3">
                    {tWaiver('full.section4.title')}
                  </h3>
                  <p>{tWaiver('full.section4.content')}</p>
                </section>

                <section>
                  <h3 className="text-white font-semibold text-base mb-3">
                    {tWaiver('full.section5.title')}
                  </h3>
                  <p>{tWaiver('full.section5.content')}</p>
                </section>

                <section>
                  <h3 className="text-white font-semibold text-base mb-3">
                    {tWaiver('full.section6.title')}
                  </h3>
                  <p>{tWaiver('full.section6.content')}</p>
                </section>

                <section>
                  <h3 className="text-white font-semibold text-base mb-3">
                    {tWaiver('full.section7.title')}
                  </h3>
                  <p>{tWaiver('full.section7.content')}</p>
                </section>

                <section>
                  <h3 className="text-white font-semibold text-base mb-3">
                    {tWaiver('full.section8.title')}
                  </h3>
                  <p>{tWaiver('full.section8.content')}</p>
                </section>

                <section>
                  <h3 className="text-white font-semibold text-base mb-3">
                    {tWaiver('full.section9.title')}
                  </h3>
                  <p>{tWaiver('full.section9.content')}</p>
                </section>

                <section className="pt-4 border-t border-stone-700">
                  <p className="text-stone-400 italic">{tWaiver('full.acknowledgment')}</p>
                </section>
              </div>
            )}
          </div>

          {/* Footer with Accept Button */}
          <div className="flex items-center justify-between pt-4 border-t border-stone-700 flex-shrink-0">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setWaiverDialogOpen(false)}
              className="text-stone-400 hover:text-white hover:bg-stone-800"
            >
              {tWaiver('close')}
            </Button>
            <Button
              type="button"
              onClick={() => {
                setWaiverAccepted(true);
                setWaiverDialogOpen(false);
              }}
              className="bg-dgr-gold text-stone-950 hover:bg-dgr-gold-light"
            >
              <Check className="w-4 h-4 mr-2" />
              {tWaiver('acceptButton')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Merchandise Preview Dialog */}
      <Dialog open={merchPreviewOpen} onOpenChange={setMerchPreviewOpen}>
        <DialogContent className="max-w-lg bg-stone-900 border-stone-700 text-white p-0 overflow-hidden">
          {merchPreviewItem?.imageUrl && (
            <div className="relative aspect-square w-full bg-stone-800">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={merchPreviewItem.imageUrl}
                alt={merchPreviewItem.name}
                className="absolute inset-0 h-full w-full object-contain"
              />
            </div>
          )}
          <div className="p-5">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-white">
                {merchPreviewItem?.name}
              </DialogTitle>
              <DialogDescription className="text-stone-400">
                {merchPreviewItem?.description}
              </DialogDescription>
            </DialogHeader>
            <div className="flex items-center justify-between mt-4">
              <p className="text-2xl font-bold text-dgr-gold">
                {merchPreviewItem?.price != null &&
                  format.number(merchPreviewItem.price, {
                    style: 'currency',
                    currency: merchPreviewItem.currencyCode,
                  })}
              </p>
              <Button
                type="button"
                onClick={() => {
                  if (merchPreviewItem) {
                    updateMerchQuantity(merchPreviewItem.id, 1);
                  }
                  setMerchPreviewOpen(false);
                }}
                className="bg-dgr-gold text-stone-950 hover:bg-dgr-gold-light"
              >
                <Plus className="w-4 h-4 mr-2" />
                {tDonate('addToOrder')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
